import React, { useState, useEffect, useTransition, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { useResumeEpoch } from '../contexts/ResumeContext';
import { useKeyboardInset } from '../components/conversation/useConversationHooks';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { toast } from 'sonner';
import { TOAST } from '../utils/toastMessages';
import { Sparkles, LogOut, Plus, X, Calendar, MapPin, Users, DollarSign, CalendarX, MessageSquare, Share2, Copy, Check, ExternalLink, CheckCircle, AlertCircle, Lightbulb, Star, Clock, Camera, MessageCircle, TrendingUp, HelpCircle, Info, CreditCard, Loader, User, Inbox, Wallet, Settings, ChevronDown, ChevronUp, ChevronRight, ListOrdered, Home, Eye, EyeOff, ShieldX, Bell, Layers, FileText, Circle } from 'lucide-react';
import FeedbackDialog from '../components/FeedbackDialog';
import ServiceAgreementDialog from '../components/ServiceAgreementDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { getPaymentStatus } from '../utils/paymentStatus';
import ConversationSwitcher from '../components/conversation/ConversationSwitcher';
import MessagingThread from '../components/MessagingThread';
import Navigation from '../components/Navigation';
import VendorInquiryCard from '../components/vendor/VendorInquiryCard';
import VendorProfileForm from '../components/vendor/VendorProfileForm';
import VendorAccountSettings from '../components/vendor/VendorAccountSettings';
import VendorInquiryDialogs from '../components/vendor/VendorInquiryDialogs';
import PaymentsTab from '../components/vendor/PaymentsTab';
import RoleSwitcher from '../components/RoleSwitcher';
import BottomTabBar from '../components/BottomTabBar';
import { normalizeImageToFile } from '../lib/normalizeImageForUpload';
import { useOnboarding } from '../context/OnboardingContext';
import { DeclarePrimaryFocus } from '../context/PrimaryFocusContext';
import ConciergeNudge from '../components/ConciergeNudge';
import HelpSupportSection from '../components/HelpSupportSection';
import PushNotificationSettings from '../components/PushNotificationSettings';
import AccountPage from '../components/AccountPage';
import ReferralRewardsCard from '../components/ReferralRewardsCard';
import OnboardingGuideCard from '../components/OnboardingGuideCard';
import { StatTile } from '../components/dashboard/StatTile';
import { DashboardHero } from '../components/dashboard/DashboardHero';
import { SectionLabel } from '../components/ui/SectionLabel';
import TermsDialog from '../components/TermsDialog';
import { InquiryListSkeleton, MessageListSkeleton } from '../components/skeletons';
import { formatShortDate } from '../utils/dateFormatter';
import { openExternalUrl } from '../utils/navigation';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, login, setActiveRole, activeRole } = useAuth();
  // Resume Engine subscription — bumps when iOS/Capacitor reports the app
  // returning to the foreground. Added to fetchData useEffect deps below
  // so the dashboard rehydrates after long background windows. The 30 s
  // refreshLiveData polling interval is intentionally NOT subscribed —
  // it polls independently. See Resume/Rehydrate Engine architecture
  // (2026-02-28).
  const resumeEpoch = useResumeEpoch();
  const { refresh: refreshOnboarding } = useOnboarding();
  const [vendorProfile, setVendorProfile] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [vendorNextSteps, setVendorNextSteps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [priceRanges, setPriceRanges] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true); // Start true for initial load skeleton
  const [blockedDates, setBlockedDates] = useState([]);
  // Booking Rules state
  const [bookingRules, setBookingRules] = useState({
    allow_same_day: false,
    same_day_cutoff_hour: 15,
    same_day_cutoff_minute: 0,
    allow_next_day: false,
    min_notice_hours: 0,
    full_payment_discount_enabled: false,
    full_payment_discount_percent: 0,
    deposit_window_hours: 48,
    allow_deposit_extensions: true,
    timezone: null
  });
  const [savingBookingRules, setSavingBookingRules] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [reportingInquiry, setReportingInquiry] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  // Profile visibility state
  const [profileVisibility, setProfileVisibility] = useState({
    vendor_active: false,
    admin_visible: true,
    profile_complete: true,
    missing_fields: [],
    is_visible_to_customers: true,
    admin_blocked: false
  });
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    email_new_inquiry: true,
    email_new_message: true,
    email_booking_updates: true,
    push_new_inquiry: true,
    push_new_message: true,
    push_booking_updates: true
  });
  const [updatingNotifications, setUpdatingNotifications] = useState(false);
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [securityAnswers, setSecurityAnswers] = useState([]);
  const [currentPasswordForSecurity, setCurrentPasswordForSecurity] = useState('');
  const [updatingSecurityQuestions, setUpdatingSecurityQuestions] = useState(false);
  const [cancellingInquiry, setCancellingInquiry] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [onHoldInquiry, setOnHoldInquiry] = useState(null);
  const [onHoldNote, setOnHoldNote] = useState('');
  const [progressNoteInquiry, setProgressNoteInquiry] = useState(null);
  const [progressNote, setProgressNote] = useState('');
  const [decliningInquiry, setDecliningInquiry] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [customDeclineReason, setCustomDeclineReason] = useState('');
  
  // NOTE: Silent mount-time role flip removed (Phase 2 of role-mode fix).
  // Cross-role navigation is now handled by the centralized RoleSwitchGuard
  // inside ProtectedRoute in App.js — it explicitly asks the user before
  // changing activeRole, so landing here never silently rewrites role state.
  
  // Inquiry notes state
  const [addingNote, setAddingNote] = useState(null);
  const [newProgressNote, setNewProgressNote] = useState('');
  const [startingWorkInquiry, setStartingWorkInquiry] = useState(null);
  const [startWorkNote, setStartWorkNote] = useState('');
  const [profileLinkCopied, setProfileLinkCopied] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [showPayoutAgreement, setShowPayoutAgreement] = useState(false);
  const [payoutAgreementChecked, setPayoutAgreementChecked] = useState(false);
  const [acceptingAgreement, setAcceptingAgreement] = useState(false);
  // Phase 4.5: track WHY the participation-agreement dialog was opened so
  // the post-acceptance continuation can route correctly.
  //   'stripe'     → continue into Stripe onboarding (existing flow)
  //   'onboarding' → return the vendor to the profile tab to keep setting up
  const [agreementIntent, setAgreementIntent] = useState('stripe');
  // Phase 4.7 terms-link fix: Terms opens IN PLACE over the agreement
  // dialog instead of navigating to /terms in a new tab (which is unreliable
  // on iOS PWA / in-app webviews). The agreement dialog stays mounted
  // underneath, so the user never loses their place.
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  // Dynamic updates/announcements from admin banners
  const [updateBanners, setUpdateBanners] = useState([]);
  const [dismissedBannerIds, setDismissedBannerIds] = useState(() => {
    try {
      const stored = localStorage.getItem('dismissedBanners');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Phase 1 Welcome tab cleanup — vendor-facing fee status pill
  const [feeStatus, setFeeStatus] = useState(null);

  // Booking and payment state
  const [bookings, setBookings] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [showServiceAgreement, setShowServiceAgreement] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(null);
  const [isPending, startTransition] = useTransition();
  const [activeTab, setActiveTabState] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check for Stripe redirect
    if (urlParams.get('stripe_success') === 'true') {
      return 'profile';
    }
    const t = urlParams.get('tab');
    // Post Chats→Requests merge: old deep-links (?tab=messages) route to Requests
    if (t === 'messages') return 'inquiries';
    return t || 'welcome';
  });

  // Profile section targeting (for "Manage Services" quick action)
  const [profileDefaultSection, setProfileDefaultSection] = useState('basic-info');
  const [activeProfileSection, setActiveProfileSection] = useState('basic-info');

  // Optimized tab change handler using transition for smoother UI
  const setActiveTab = useCallback((newTab) => {
    startTransition(() => {
      setActiveTabState(newTab);
    });
    // Scroll to top when changing tabs (outside transition for immediate effect)
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Sync tab state when URL ?tab= param changes via React Router navigation
  // (e.g., from nudge buttons or external links). Uses a ref to avoid fighting
  // with direct onTabChange calls from BottomTabBar.
  const lastUrlTab = React.useRef(searchParams.get('tab'));
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && urlTab !== lastUrlTab.current) {
      lastUrlTab.current = urlTab;
      // Post Chats→Requests merge: redirect stale 'messages' → 'inquiries'
      const resolvedTab = urlTab === 'messages' ? 'inquiries' : urlTab;
      setActiveTabState(resolvedTab);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [searchParams]);

  // Messaging state
  const [showMessaging, setShowMessaging] = useState(false);
  const [selectedInquiryForMessaging, setSelectedInquiryForMessaging] = useState(null);
  
  // How Celey Works guide state - smart visibility based on onboarding status
  // - Expanded for new/incomplete providers (first 7-14 days or incomplete profile)
  // - Collapsed once onboarding complete and provider is "active"
  // - Hidden if dismissed (persists per user)
  const [howCeleyWorksDismissed, setHowCeleyWorksDismissed] = useState(() => {
    return localStorage.getItem('howCeleyWorksDismissed') === 'true';
  });
  const [howCeleyWorksExpanded, setHowCeleyWorksExpanded] = useState(true); // Will be set based on onboarding status
  
  // Platform Updates state - collapsible with persistence
  const [platformUpdatesExpanded, setPlatformUpdatesExpanded] = useState(() => {
    const stored = localStorage.getItem('platformUpdatesExpanded');
    // Default expanded for new providers (null = not set = new)
    return stored === null ? true : stored === 'true';
  });
  
  // Getting Started Checklist state - only collapsible when COMPLETE
  const [checklistExpanded, setChecklistExpanded] = useState(true); // Controlled by completion status
  const [checklistDismissed, setChecklistDismissed] = useState(() => {
    return localStorage.getItem('checklistDismissed') === 'true';
  });
  
  // How Jobs Work guide state — default COLLAPSED
  const [showJobsGuide, setShowJobsGuide] = useState(() => {
    return localStorage.getItem('vendorJobsGuideExpanded') === 'true';
  });
  
  // Inquiry Card View Mode: 'compact' | 'detailed' — persisted in localStorage.
  // Legacy 'simple' is silently migrated to 'detailed' (Standard is a strict
  // superset of Simple, so no information is lost).
  const [inquiryViewMode, setInquiryViewMode] = useState(() => {
    const v = localStorage.getItem('vendorInquiryViewMode');
    if (v === 'simple') {
      localStorage.setItem('vendorInquiryViewMode', 'detailed');
      return 'detailed';
    }
    return (v === 'detailed' || v === 'compact') ? v : 'detailed';
  });

  // Sort mode for Requests: 'default' (backend order) or 'recent' (by latest activity)
  const [inquirySortMode, setInquirySortMode] = useState(() => {
    return localStorage.getItem('vendorInquirySortMode') === 'recent' ? 'recent' : 'default';
  });

  // One-time "Chats merged into Requests" transition notice
  const [showChatsMergedNotice, setShowChatsMergedNotice] = useState(() => {
    return localStorage.getItem('celey:chats-merged-dismissed') !== '1';
  });
  const dismissChatsMergedNotice = () => {
    localStorage.setItem('celey:chats-merged-dismissed', '1');
    setShowChatsMergedNotice(false);
  };

  // Request lifecycle filter for Requests tab
  const [requestsFilter, setRequestsFilter] = useState('all');
  // Schedule → Requests handoff: when a vendor taps a Schedule row we mark
  // its inquiry id here so the matching card in the Requests list can
  // scroll itself into view and briefly flash. Reset after the highlight.
  const [highlightInquiryId, setHighlightInquiryId] = useState(null);
  const filterScrollRef = React.useRef(null);

  // Set view mode (Compact or Standard). Legacy 'simple' is migrated to
  // 'detailed' silently elsewhere; this setter only accepts the two live modes.
  const setInquiryViewModeAndPersist = (mode) => {
    if (mode !== 'detailed' && mode !== 'compact') return;
    setInquiryViewMode(mode);
    localStorage.setItem('vendorInquiryViewMode', mode);
  };

  // Toggle sort mode and persist
  const toggleInquirySortMode = () => {
    const next = inquirySortMode === 'recent' ? 'default' : 'recent';
    setInquirySortMode(next);
    localStorage.setItem('vendorInquirySortMode', next);
  };

  // Auto-scroll active filter chip into view when filter changes or tab switches
  React.useEffect(() => {
    if (activeTab !== 'inquiries') return;
    const timer = setTimeout(() => {
      const container = filterScrollRef.current;
      if (!container) return;
      const activeChip = container.querySelector(`[data-testid="filter-${requestsFilter}"]`);
      if (!activeChip) return;
      const containerRect = container.getBoundingClientRect();
      const chipRect = activeChip.getBoundingClientRect();
      const scrollLeft = activeChip.offsetLeft - (containerRect.width / 2) + (chipRect.width / 2);
      container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [requestsFilter, activeTab]);

  // Welcome-tab Fee Status Pill — fetch once on mount (non-blocking, silent on error)
  React.useEffect(() => {
    const fetchFeeStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/vendors/me/fee-status`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        setFeeStatus(res.data);
      } catch { /* informational only */ }
    };
    fetchFeeStatus();
  }, []);

  // ── Schedule tab internal segmented control (Upcoming | Calendar | Booking Rules)
  const [scheduleSegment, setScheduleSegment] = useState(() => {
    try { return localStorage.getItem('scheduleSegment') || 'upcoming'; }
    catch { return 'upcoming'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('scheduleSegment', scheduleSegment); } catch { /* noop */ }
  }, [scheduleSegment]);
  // Day-detail overlay for Calendar tap
  const [scheduleDayDetail, setScheduleDayDetail] = useState(null); // ISO date string or null
  // Booking Rules focused-editor drawer (hybrid tap-to-edit model)
  const [rulesEditor, setRulesEditor] = useState(null); // 'min_notice' | 'discount' | 'deposit' | null
  // iOS keyboard inset for the rules drawer. Uses the project-standard
  // `useKeyboardInset` hook which subscribes to the Capacitor `Keyboard`
  // plugin on native (correct under `Keyboard.resize: "none"`, where
  // `visualViewport` does NOT report keyboard changes) and falls back to
  // `visualViewport` on web. Same hook powers MessagingThread.
  const rulesKeyboardInset = useKeyboardInset(!!rulesEditor);

  // Close any Schedule modal on tab navigation — modal state must not persist across routes
  React.useEffect(() => {
    if (activeTab !== 'calendar') {
      setScheduleDayDetail(null);
      setRulesEditor(null);
    }
  }, [activeTab]);

  // Auto-clear the Schedule → Requests highlight after the flash so revisits
  // to the Requests tab don't stay decorated indefinitely.
  React.useEffect(() => {
    if (!highlightInquiryId) return;
    const t = setTimeout(() => setHighlightInquiryId(null), 2200);
    return () => clearTimeout(t);
  }, [highlightInquiryId]);
  
  const [profileData, setProfileData] = useState({
    business_name: '',
    contact_person: '',
    city: '',
    state: '',
    zip_code: '',
    service_radius: 25,
    bio: '',
    short_tagline: '',
    profile_photo: '',
    cover_image: '',
    portfolio_photos: ['', '', ''],
    email: '',
    phone: '',
    website: '',
    instagram: '',
    tiktok: '',
    facebook: '',
    categories: [],
    primary_service: '',
    vendor_type: null,
    starting_price: [],
    payment_methods: [],
    blocked_dates: [],
    specialties: [],
    services: []
  });
  const [templateApplied, setTemplateApplied] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath; // External URL
    }
    // Uploaded images are served at /api/uploads, so prepend backend URL directly
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    return `${backendUrl}${imagePath}`;
  };

  // Narrow background refresh for the 30s polling interval.
  // INTENTIONALLY does NOT touch profileData / vendorProfile / blockedDates /
  // bookingRules — polling must never clobber in-progress profile edits.
  // Purpose (per original interval intent): surface new inquiries/messages and
  // live visibility changes.
  const refreshLiveData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    // 15 s per-call timeout — guards against iOS WKWebView resume / cold-
    // start hangs where un-timed XHRs can stay suspended indefinitely.
    // Audit 2026-02-28.
    const reqOpts = { headers, timeout: 15000 };

    try {
      const inquiriesRes = await axios.get(`${API}/inquiries/vendor`, reqOpts);
      setInquiries(inquiriesRes.data);
    } catch (_) { /* keep previous state on transient failure */ }

    // Soft-action vendor Next Steps (e.g., revise rejected proposals).
    // Failure is non-blocking — list simply stays empty.
    try {
      const stepsRes = await axios.get(`${API}/vendor/next-steps`, reqOpts);
      setVendorNextSteps(Array.isArray(stepsRes.data) ? stepsRes.data : []);
    } catch (_) { setVendorNextSteps([]); }

    try {
      const bookingsRes = await axios.get(`${API}/bookings?role=vendor`, reqOpts);
      setBookings(bookingsRes.data);
    } catch (_) { /* keep previous state */ }

    try {
      const visibilityRes = await axios.get(`${API}/vendors/me/visibility`, reqOpts);
      setProfileVisibility(visibilityRes.data);
    } catch (_) { /* keep previous state */ }
  };

  useEffect(() => {
    // Redirect admins to admin dashboard
    if (user?.is_admin) {
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
    fetchSecurityQuestions();
    checkOnboardingStatus();
    fetchUpdateBanners();
    
    // Check for new signup welcome message
    const isNewSignup = sessionStorage.getItem('celey:new_signup');
    if (isNewSignup) {
      sessionStorage.removeItem('celey:new_signup');
      // Show welcome message after a brief delay so the page loads first.
      // This is the ONE welcome toast — AuthPage intentionally does NOT
      // fire its own to avoid stacked duplicates.
      setTimeout(() => {
        toast.success(TOAST.WELCOME_VENDOR, { duration: 5000 });
      }, 500);
    }
    
    // Background polling: ONLY refreshes live data (inquiries, bookings,
    // visibility). Does NOT rehydrate profileData — prevents clobbering
    // unsaved profile-form edits (e.g. short_tagline).
    const intervalId = setInterval(() => {
      refreshLiveData();
    }, 30000); // 30 seconds
    
    return () => clearInterval(intervalId);
  }, [user, navigate, activeRole, resumeEpoch]); // Re-fetch on role change + on app resume

  // Calculate onboarding completion status
  const getOnboardingCompletion = () => {
    // Use profile strength score from onboarding status
    const strength = onboardingStatus?.profile_strength;
    if (strength) {
      const score = strength.score || 0;
      return { 
        score,
        isComplete: score >= 90,
        missing: strength.missing_items || [],
        breakdown: strength.breakdown || {},
        badge: strength.badge,
      };
    }
    // Fallback if profile_strength not yet available
    return { score: 0, isComplete: false, missing: [], breakdown: {}, badge: null };
  };

  // Update section states based on onboarding completion
  useEffect(() => {
    if (onboardingStatus && vendorProfile !== undefined) {
      const { isComplete } = getOnboardingCompletion();
      
      // Getting Started Checklist: NOT collapsible when incomplete, auto-collapse when complete
      setChecklistExpanded(!isComplete);
      
      const createdAt = vendorProfile?.created_at ? new Date(vendorProfile.created_at) : null;
      const daysSinceSignup = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)) : 0;
      const isNewProvider = daysSinceSignup < 14;
      
      const isActive = isComplete && onboardingStatus.stripe_connected;
      
      setHowCeleyWorksExpanded(isNewProvider || !isActive);
    }  }, [onboardingStatus, vendorProfile]);

  // Handler to dismiss "How Celey Works" permanently
  const dismissHowCeleyWorks = () => {
    setHowCeleyWorksDismissed(true);
    localStorage.setItem('howCeleyWorksDismissed', 'true');
  };

  // Handler to toggle "How Celey Works" expanded/collapsed
  const toggleHowCeleyWorks = () => {
    setHowCeleyWorksExpanded(!howCeleyWorksExpanded);
  };

  // Handler to toggle Platform Updates expanded/collapsed
  const togglePlatformUpdates = () => {
    const newState = !platformUpdatesExpanded;
    setPlatformUpdatesExpanded(newState);
    localStorage.setItem('platformUpdatesExpanded', newState.toString());
  };

  // Fetch dynamic update banners from admin system
  const fetchUpdateBanners = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/banners/active`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      // Filter to dashboard-placed banners not dismissed by user
      const stored = localStorage.getItem('dismissedBanners');
      const dismissed = stored ? JSON.parse(stored) : [];
      const active = (response.data || []).filter(b =>
        b.placement === 'dashboard_only' && !dismissed.includes(b.id)
      );
      setUpdateBanners(active);
    } catch (error) {
      console.error('Error fetching update banners:', error);
    }
  };

  // Dismiss a banner card
  const dismissBannerCard = async (bannerId) => {
    const newDismissed = [...dismissedBannerIds, bannerId];
    setDismissedBannerIds(newDismissed);
    localStorage.setItem('dismissedBanners', JSON.stringify(newDismissed));
    setUpdateBanners(prev => prev.filter(b => b.id !== bannerId));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/banners/${bannerId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail — localStorage is primary
    }
  };

  // Handler to toggle Getting Started Checklist (only works when complete)
  const toggleChecklist = () => {
    const { isComplete } = getOnboardingCompletion();
    if (isComplete) {
      setChecklistExpanded(!checklistExpanded);
    }
  };

  // Handler to dismiss checklist (only allowed when complete + has activity)
  const dismissChecklist = () => {
    const { isComplete } = getOnboardingCompletion();
    // Only allow dismiss if: complete AND (has inquiries OR 14+ days active)
    const createdAt = vendorProfile?.created_at ? new Date(vendorProfile.created_at) : null;
    const daysSinceSignup = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)) : 0;
    const canDismiss = isComplete && (inquiries.length > 0 || daysSinceSignup >= 14);
    
    if (canDismiss) {
      setChecklistDismissed(true);
      localStorage.setItem('checklistDismissed', 'true');
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    // 15 s per-call timeout — guards against iOS WKWebView resume / cold-
    // start hangs where un-timed XHRs can stay suspended indefinitely
    // and leave `loading` stuck at true (skeletons forever). Each call
    // already has its own try/catch; the timeout converts the silent hang
    // into a normal ECONNABORTED rejection that the existing handlers
    // already process. Audit 2026-02-28.
    const reqOpts = { headers, timeout: 15000 };

    // Fetch profile and inquiries (these may fail for new service providers)
    try {
      const profileRes = await axios.get(`${API}/vendors/me/profile`, reqOpts);
      if (profileRes.data) {
        setVendorProfile(profileRes.data);
        setTemplateApplied(true); // Existing vendor — skip template auto-apply
        const serverData = {
          ...profileRes.data,
          starting_price: Array.isArray(profileRes.data.starting_price) ? profileRes.data.starting_price : [],
          payment_methods: Array.isArray(profileRes.data.payment_methods) ? profileRes.data.payment_methods : []
        };
        // Defensive merge: when the server omits a key (undefined/null), fall
        // back to the local value so initial form defaults and any unsaved
        // free-text edits aren't wiped. Empty string ("") from server is
        // respected — it means the vendor intentionally cleared the field.
        setProfileData(prev => ({
          ...prev,
          ...serverData,
          short_tagline:   serverData.short_tagline   ?? prev.short_tagline   ?? '',
          bio:             serverData.bio             ?? prev.bio             ?? '',
          primary_service: serverData.primary_service ?? prev.primary_service ?? '',
          cover_image:     serverData.cover_image     ?? prev.cover_image     ?? '',
          business_name:   serverData.business_name   ?? prev.business_name   ?? '',
          contact_person:  serverData.contact_person  ?? prev.contact_person  ?? '',
          city:            serverData.city            ?? prev.city            ?? '',
          zip_code:        serverData.zip_code        ?? prev.zip_code        ?? '',
          website:         serverData.website         ?? prev.website         ?? '',
          instagram:       serverData.instagram       ?? prev.instagram       ?? '',
          tiktok:          serverData.tiktok          ?? prev.tiktok          ?? '',
          facebook:        serverData.facebook        ?? prev.facebook        ?? '',
        }));
        setBlockedDates((profileRes.data.blocked_dates || []).map(d => new Date(d)));
        
        // Fetch notification settings
        try {
          const notifRes = await axios.get(`${API}/vendors/me/notification-settings`, reqOpts);
          setNotificationSettings(notifRes.data);
        } catch (notifErr) {
          console.log('Could not fetch notification settings');
        }
        
        // Fetch booking rules
        try {
          const rulesRes = await axios.get(`${API}/vendors/me/booking-rules`, reqOpts);
          setBookingRules(rulesRes.data);
          
          // Auto-detect and persist timezone if not set
          if (!rulesRes.data.timezone) {
            const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (detectedTimezone) {
              try {
                await axios.patch(`${API}/vendors/me/timezone`, 
                  { timezone: detectedTimezone },
                  { headers }
                );
                console.log('Auto-detected and saved timezone:', detectedTimezone);
                // Update local state
                setBookingRules(prev => ({ ...prev, timezone: detectedTimezone }));
              } catch (tzErr) {
                console.log('Could not save auto-detected timezone:', tzErr);
              }
            }
          }
        } catch (rulesErr) {
          console.log('Could not fetch booking rules');
        }
      }
      
      // Fetch visibility status (always, even for new vendors)
      try {
        const visibilityRes = await axios.get(`${API}/vendors/me/visibility`, reqOpts);
        setProfileVisibility(visibilityRes.data);
      } catch (visErr) {
        // New vendor with no doc — keep default (vendor_active: false)
        console.log('No visibility data yet');
      }
    } catch (error) {
      console.log('No service provider profile yet (new service provider)');
      // Pre-fill email and phone from user account for new vendors
      if (user) {
        setProfileData(prev => ({
          ...prev,
          email: user.email || '',
          phone: user.phone || ''
        }));
      }
    }

    try {
      const inquiriesRes = await axios.get(`${API}/inquiries/vendor`, reqOpts);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error.response?.status, error.response?.data);
      if (error.response?.status === 404) {
        // No vendor profile yet, which is expected for new vendors
        console.log('No vendor profile found - complete your profile first');
      }
      setInquiries([]);
    }

    // Initial load of vendor soft-action Next Steps. Non-blocking.
    try {
      const stepsRes = await axios.get(`${API}/vendor/next-steps`, reqOpts);
      setVendorNextSteps(Array.isArray(stepsRes.data) ? stepsRes.data : []);
    } catch (_) { setVendorNextSteps([]); }

    // Fetch bookings - pass role=vendor for dual-role users
    try {
      const bookingsRes = await axios.get(`${API}/bookings?role=vendor`, reqOpts);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.log('No bookings yet');
      setBookings([]);
    }

    // Fetch reference data (these should always work)
    try {
      const [categoriesRes, statesRes, priceRes, paymentRes] = await Promise.all([
        axios.get(`${API}/categories`, { timeout: 15000 }),
        axios.get(`${API}/states`, { timeout: 15000 }),
        axios.get(`${API}/price-ranges`, { timeout: 15000 }),
        axios.get(`${API}/payment-methods`, { timeout: 15000 })
      ]);

      setCategories(categoriesRes.data.categories);
      setStates(statesRes.data.states);
      setPriceRanges(priceRes.data.price_ranges);
      setPaymentMethods(paymentRes.data.payment_methods);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      toast.error('Failed to load form data. Please refresh the page.');
    } finally {
      setLoading(false); // Always clear loading state after all fetches
    }
  };

  // Onboarding and booking functions
  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setOnboardingStatus(response.data);
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      
      // If profile doesn't exist (404) or any error, set default onboarding status
      const defaultOnboardingStatus = {
        onboarding_completed: false,
        onboarding_step: 0,
        participation_agreement_accepted: false,
        participation_agreement_accepted_at: null,
        stripe_connected: false,
        stripe_onboarding_complete: false,
        stripe_needs_setup: false,
        stripe_is_test_account: false,
        api_key_type: "LIVE",
        stripe_account_id_masked: null,
        charges_enabled: false,
        payouts_enabled: false,
        details_submitted: false
      };
      setOnboardingStatus(defaultOnboardingStatus);
    }
  };

  // Handle Stripe connect/reconnect - Shows agreement first if not yet accepted
  const handleStripeConnect = async () => {
    // If vendor hasn't accepted the participation agreement, show it first
    if (onboardingStatus && !onboardingStatus.participation_agreement_accepted) {
      setAgreementIntent('stripe');
      setPayoutAgreementChecked(false);
      setShowPayoutAgreement(true);
      return;
    }
    proceedToStripeConnect();
  };

  const proceedToStripeConnect = async () => {
    setStripeConnecting(true);
    toast.info('Opening Stripe setup...');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/vendor/stripe-onboarding-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      const stripeUrl = data.onboarding_url || data.url;
      
      if (stripeUrl) {
        await openExternalUrl(stripeUrl);
        toast.success('Stripe setup opened. Complete the setup there and return here.');
      } else if (data.detail) {
        toast.error(data.detail);
      } else {
        toast.error('Failed to get Stripe setup link');
      }
    } catch (error) {
      console.error('Stripe connect error:', error);
      toast.error('Failed to connect to Stripe. Please try again.');
    } finally {
      setStripeConnecting(false);
    }
  };

  const handleAcceptPayoutAgreement = async () => {
    setAcceptingAgreement(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/vendor/accept-participation-agreement`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to accept agreement');
      }
      setShowPayoutAgreement(false);
      // Refresh both the legacy onboardingStatus (used by this page) and the
      // OnboardingContext (source of truth for the guide card + strip) so
      // no surface shows stale role_granted messaging after acceptance.
      await checkOnboardingStatus();
      try { refreshOnboarding && refreshOnboarding(); } catch (_) { /* noop */ }

      // Branch on WHY the dialog was opened (Phase 4.5 flow correction).
      if (agreementIntent === 'onboarding') {
        // Fresh vendor flow — land them in profile completion, not Stripe.
        toast.success('Agreement accepted. Finish your profile next.');
        setActiveTab('profile');
      } else {
        // Legacy Stripe-first flow — continue to Stripe.
        proceedToStripeConnect();
      }
    } catch (error) {
      console.error('Agreement acceptance error:', error);
      toast.error(error.message || 'Failed to accept agreement');
    } finally {
      setAcceptingAgreement(false);
    }
  };

  // Phase 4.5: if the guide card / strip routed here with the onboarding
  // action flag, open the participation-agreement dialog directly so the
  // vendor never has to hunt for the real first blocker. The flag is
  // honoured only when the agreement has not yet been accepted.
  useEffect(() => {
    const action = searchParams.get('onboarding_action');
    if (action !== 'agreement') return;
    if (!onboardingStatus) return; // wait until status loads
    if (onboardingStatus.participation_agreement_accepted) {
      // Already accepted — just clean up the URL flag silently.
      const params = new URLSearchParams(window.location.search);
      params.delete('onboarding_action');
      const qs = params.toString();
      window.history.replaceState({}, '', `/vendor/dashboard${qs ? `?${qs}` : ''}`);
      return;
    }
    // Open the existing Platform Fee / Participation agreement dialog in
    // onboarding mode. Clear the URL flag so a page refresh doesn't reopen it.
    setAgreementIntent('onboarding');
    setPayoutAgreementChecked(false);
    setShowPayoutAgreement(true);
    const params = new URLSearchParams(window.location.search);
    params.delete('onboarding_action');
    const qs = params.toString();
    window.history.replaceState({}, '', `/vendor/dashboard${qs ? `?${qs}` : ''}`);
  }, [searchParams, onboardingStatus]);

  // Check for Stripe redirect on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_success') === 'true') {
      toast.success('Stripe account connected successfully! You can now receive payments.');
      // Clean up URL
      window.history.replaceState({}, '', '/vendor/dashboard?tab=profile');
      // Refresh data to get updated Stripe status
      fetchData();
      checkOnboardingStatus();
    } else if (urlParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe setup to receive payments.');
      window.history.replaceState({}, '', '/vendor/dashboard?tab=profile');
    }
  }, []);

  const getBookingForInquiry = (inquiryId) => {
    return bookings.find(b => b.inquiry_id === inquiryId);
  };

  // ── Lifecycle predicate helpers ──────────────────────────────────────────
  // Single source of truth for the Vendor Dashboard's filter/tile chips.
  // Previously these were duplicated 3× inline (tile section, chip section,
  // filter section) which drifted and caused the Booked chip to exclude
  // paid-in-full future bookings while All still showed them.
  // No backend status renaming; pure derived logic.
  const CLOSED_INQUIRY_STATUSES = ['declined', 'cancelled', 'completed'];
  const CANCELLED_BOOKING_STATUSES = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'];

  const isInquiryClosed = (i) => CLOSED_INQUIRY_STATUSES.includes(i?.status);
  const isInquiryCanceled = (i) => ['declined', 'cancelled'].includes(i?.status);

  const isReadyToCloseInquiry = (i) => {
    const bk = getBookingForInquiry(i.id);
    if (!bk) return false;
    if (['completed', 'completed_by_vendor', ...CANCELLED_BOOKING_STATUSES].includes(bk.status)) return false;
    // Ready To Close Classification Fix (2026-06-01): include unpaid +
    // window-expired + past-event rows in Ready to Close. The 2026-05-30
    // exclusion assumed those rows would "naturally" land in In Progress;
    // they actually fell into Pending Payment because inquiry.status stays
    // 'accepted' until a closing action runs. That inflated the Pending
    // Payment chip with unrecoverable rows (customer can no longer pay,
    // window is past, event is past) and hid the close-out CTA the
    // vendor needs. They now live in Ready to Close where the card-level
    // CTA cascade already serves the correct action: Cancel & Close for
    // unpaid-closeout rows (via lifecycle.closeout.reason →
    // VendorInquiryCard getPrimaryCTA line 258), Mark Complete for paid
    // rows (line 261). No new state, no new chip, no backend change.
    if (!i.event_date) return false;
    return new Date() > new Date(i.event_date);
  };

  // Phase-1 fix: a "booked" inquiry is any deposit-paid booking that has not
  // yet been completed or cancelled AND has not crossed the event date (those
  // belong in Ready to Close). The previous predicate also required
  // `!final_paid`, which silently dropped paid-in-full future-event bookings
  // out of the Booked chip — they only appeared under All. That gap is
  // closed here. Used by BOTH the tile count and the Requests chip predicate
  // so they cannot drift again.
  const isInquiryBooked = (i) => {
    const bk = getBookingForInquiry(i.id);
    if (!bk?.deposit_paid) return false;
    if (['completed', 'completed_by_vendor', ...CANCELLED_BOOKING_STATUSES].includes(bk.status)) return false;
    if (isReadyToCloseInquiry(i)) return false;
    return true;
  };

  // ── Centralised lifecycle classifier ─────────────────────────────────────
  // Single authoritative state per inquiry. Used by every chip predicate,
  // tile count, and the Schedule → Requests handoff so a row can never
  // appear under two contradictory chips (e.g., a canceled inquiry leaking
  // into Booked, or a past-event row still showing under Payments).
  //
  // Priority is strict and matches the approved audit:
  //   canceled > completed > ready_to_close > booked > pending_payment
  //   > in_progress > needs_response > other
  //
  // `pending_payment` here only covers `status === 'accepted'` with no
  // deposit_paid booking yet. Payment-urgency overlays (deposit_expired,
  // final_overdue, etc.) are layered on top via the `payments` chip,
  // which explicitly excludes terminal states.
  const getInquiryLifecycleState = (inq) => {
    if (!inq) return 'other';
    if (isInquiryCanceled(inq)) return 'canceled';
    if (inq.status === 'completed') return 'completed';
    if (isReadyToCloseInquiry(inq)) return 'ready_to_close';
    const bk = getBookingForInquiry(inq.id);
    if (bk?.deposit_paid && !CANCELLED_BOOKING_STATUSES.includes(bk.status)) return 'booked';
    if (inq.status === 'accepted') return 'pending_payment';
    if (['proposal_sent', 'reviewing', 'in_progress', 'agreement_signed', 'on_hold'].includes(inq.status)) return 'in_progress';
    if (inq.status === 'pending') return 'needs_response';
    return 'other';
  };

  // Schedule-tap target filter resolver. Routes Schedule rows to the chip
  // that already shows that lifecycle state so the matching card flashes
  // and back-tap returns cleanly. Chat opens only via Message.
  const getFilterForInquiry = (i) => {
    const state = getInquiryLifecycleState(i);
    return state === 'other' ? 'all' : state;
  };

  const handleServiceAgreementAccepted = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bookings/${selectedBooking.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedBooking = response.data;
      setSelectedBooking(updatedBooking);
      
      if (updatedBooking.service_agreement_accepted_by_customer && 
          updatedBooking.service_agreement_accepted_by_vendor) {
        toast.success('Agreement accepted! Customer can now pay the deposit.');
      } else {
        toast.success('Agreement accepted! Waiting for customer to accept.');
      }
      
      await fetchData();
    } catch (error) {
      console.error('Error checking booking status:', error);
    }
  };

  const handleMarkComplete = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/bookings/${bookingId}/mark-completed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Check if final payment was already made
      const booking = bookings.find(b => b.id === bookingId);
      if (booking?.final_paid) {
        toast.success('Job marked as complete! Payment has already been received.');
      } else {
        toast.success('Job marked as complete! Customer will be notified to review and pay final amount.');
      }
      setMarkingComplete(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark complete');
    }
  };

  // Messaging handlers
  const handleOpenMessaging = (inquiry) => {
    setSelectedInquiryForMessaging(inquiry);
    setShowMessaging(true);
  };

  // Memoized close handler to prevent unnecessary re-renders in MessagingThread
  const handleCloseMessaging = useCallback(() => {
    setShowMessaging(false);
  }, []);

  const handleProposalAccepted = async (booking) => {
    // Proposal accepted by customer, booking created
    setBookings([...bookings, booking]);
    setShowMessaging(false);
    toast.success('Customer accepted your proposal! Waiting for service agreement signatures.');
    await fetchData();
  };
  
  const handleBookingCancelled = async (cancelledBooking) => {
    // Update bookings state with cancelled booking
    setBookings(bookings.map(b => 
      b.id === cancelledBooking.id ? cancelledBooking : b
    ));
    // Refresh all data to get updated inquiry status
    await fetchData();
  };
  
  const handleBookingUpdated = async (updatedBooking) => {
    // Update bookings state with the updated booking
    setBookings(bookings.map(b => 
      b.id === updatedBooking.id ? updatedBooking : b
    ));
    // Refresh all data to get the latest state
    await fetchData();
  };

  const updateInquiryStatus = async (inquiryId, newStatus, reason = null) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const payload = { status: newStatus };
      if (reason) {
        payload.cancellation_reason = reason;
      }
      
      await axios.patch(
        `${API}/inquiries/${inquiryId}/status`,
        payload,
        { headers }
      );
      
      const statusLabel = newStatus === 'cancelled' ? 'did not move forward' : newStatus;
      toast.success(`Inquiry marked as ${statusLabel}`);
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inquiry status');
    }
  };

  const handleCancelInquiry = () => {
    if (cancellingInquiry) {
      updateInquiryStatus(cancellingInquiry.id, 'cancelled', cancellationReason || 'Not specified');
      setCancellingInquiry(null);
      setCancellationReason('');
    }
  };

  const updateEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (newEmail === user?.email) {
      toast.error('New email is the same as current email');
      return;
    }

    if (!window.confirm(`Change your email to ${newEmail}? You'll need to use this email to login.`)) {
      return;
    }

    setUpdatingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/auth/update-email`,
        { new_email: newEmail },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.EMAIL_UPDATED);
      setNewEmail('');
      // Update local user state
      const updatedUser = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      login(updatedUser.data, token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const updatePhone = async () => {
    if (!newPhone.trim()) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (newPhone === user?.phone) {
      toast.error('New phone number is the same as current phone number');
      return;
    }

    if (!window.confirm(`Change your phone number to ${newPhone}?`)) {
      return;
    }

    setUpdatingPhone(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/auth/update-phone`,
        { new_phone: newPhone },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.PHONE_NUMBER_UPDATED);
      setNewPhone('');
      // Update local user state
      const updatedUser = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      login(updatedUser.data, token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone number');
    } finally {
      setUpdatingPhone(false);
    }
  };

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`, { timeout: 15000 });
      setSecurityQuestions(response.data.questions);
      setSecurityAnswers(response.data.questions.map(q => ({ question: q, answer: '' })));
    } catch (error) {
      console.error('Failed to load security questions:', error);
    }
  };

  // Toggle profile visibility (vendor_active)
  const toggleProfileVisibility = async (newActiveState) => {
    if (profileVisibility.admin_blocked) {
      toast.error('Your profile has been hidden by Celey admin. Please contact support.');
      return;
    }

    setUpdatingVisibility(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/vendors/me/visibility`,
        { vendor_active: newActiveState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfileVisibility({
        vendor_active: response.data.vendor_active,
        admin_visible: response.data.admin_visible,
        is_visible_to_customers: response.data.is_visible_to_customers,
        admin_blocked: !response.data.admin_visible
      });
      
      toast.success(newActiveState ? 'Profile activated! You\'re now visible to customers.' : 'Profile deactivated. You\'re hidden from customers.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    } finally {
      setUpdatingVisibility(false);
    }
  };

  // Update notification setting
  const updateNotificationSetting = async (key, value) => {
    setUpdatingNotifications(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/vendors/me/notification-settings`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotificationSettings(prev => ({
        ...prev,
        [key]: value
      }));
      
      toast.success(TOAST.NOTIFICATION_PREFERENCE_UPDATED);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update notification settings');
      // Revert optimistic update
      setNotificationSettings(prev => ({
        ...prev,
        [key]: !value
      }));
    } finally {
      setUpdatingNotifications(false);
    }
  };

  const submitProgressNote = async (inquiryId) => {
    if (!newProgressNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/inquiries/${inquiryId}/progress-notes`,
        {
          note: newProgressNote,
          author_role: 'vendor'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(TOAST.NOTE_ADDED);
      setAddingNote(null);
      setNewProgressNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 10) {
      toast.error('New password must be at least 10 characters');
      return;
    }

    setUpdatingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.PASSWORD_CHANGED);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSecurityQuestionsUpdate = async (e) => {
    e.preventDefault();

    if (!currentPasswordForSecurity) {
      toast.error('Please enter your current password');
      return;
    }

    const filledAnswers = securityAnswers.filter(a => a.answer.trim() !== '');
    if (filledAnswers.length !== 3) {
      toast.error('Please answer all 3 security questions');
      return;
    }

    setUpdatingSecurityQuestions(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/update-security-questions`,
        {
          current_password: currentPasswordForSecurity,
          security_answers: securityAnswers
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.SECURITY_QUESTIONS_UPDATED);
      setCurrentPasswordForSecurity('');
      setSecurityAnswers(securityQuestions.map(q => ({ question: q, answer: '' })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update security questions');
    } finally {
      setUpdatingSecurityQuestions(false);
    }
  };

  // Vendor decline inquiry (simple decline with reason)
  const handleDeclineInquiry = async () => {
    if (!declineReason) {
      toast.error('Please select a reason');
      return;
    }
    
    if (declineReason === "Other" && !customDeclineReason.trim()) {
      toast.error('Please explain why you can\'t help');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Use custom reason if "Other" is selected, otherwise use the preset reason
      const finalReason = declineReason === "Other" ? customDeclineReason : declineReason;
      
      await axios.patch(
        `${API}/inquiries/${decliningInquiry.id}/decline`,
        { reason: finalReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inquiry declined. Customer has been notified.');
      setDecliningInquiry(null);
      setDeclineReason('');
      setCustomDeclineReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline inquiry');
    }
  };
  
  // Old decline inquiry (deprecated)
  const declineInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to decline this inquiry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/inquiries/${inquiryId}/vendor-accept`,
        { accepted: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inquiry declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline inquiry');
    }
  };

  // Mark in progress
  const openStartWorkDialog = (inquiry) => {
    setStartingWorkInquiry(inquiry);
    setStartWorkNote('');
  };

  const markAsReviewing = async (inquiryId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/inquiries/${inquiryId}/status`,
        { status: 'reviewing' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inquiry marked as reviewing');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const markInProgress = async () => {
    if (!startWorkNote.trim()) {
      toast.error('Please add a note about starting work on this inquiry');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Update status to in_progress
      await axios.patch(
        `${API}/inquiries/${startingWorkInquiry.id}/status`,
        { status: 'in_progress' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add progress note
      await axios.post(
        `${API}/inquiries/${startingWorkInquiry.id}/progress-notes`,
        {
          note: startWorkNote,
          author_role: 'vendor'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Work started! Status updated to In Progress');
      setStartingWorkInquiry(null);
      setStartWorkNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start work');
    } finally {
      setLoading(false);
    }
  };

  // Put on hold
  const putOnHold = async () => {
    if (!onHoldNote.trim()) {
      toast.error('Please enter a reason for putting this inquiry on hold');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/inquiries/${onHoldInquiry.id}/status`,
        { status: 'on_hold', on_hold_note: onHoldNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inquiry put on hold');
      setOnHoldInquiry(null);
      setOnHoldNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Add progress note
  const addProgressNote = async () => {
    if (!progressNote.trim()) {
      toast.error('Please enter a progress note');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/inquiries/${progressNoteInquiry.id}/progress-notes`,
        { note: progressNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Progress note added');
      setProgressNoteInquiry(null);
      setProgressNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    }
  };

  // Mark completed
  const markCompleted = async (inquiryId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Find the booking for this inquiry
      const booking = bookings.find(b => b.inquiry_id === inquiryId);
      
      if (booking) {
        // Mark booking as completed by vendor
        await axios.post(
          `${API}/bookings/${booking.id}/mark-completed`,
          {},
          { headers }
        );
        // Check if final payment was already made
        if (booking.final_paid) {
          toast.success('Job marked as complete! Payment has already been received.');
        } else {
          toast.success('Job marked as complete! Customer will be notified to review and pay final amount.');
        }
      } else {
        // Fallback: just update inquiry status
        await axios.patch(
          `${API}/inquiries/${inquiryId}/status`,
          { status: 'completed' },
          { headers }
        );
        toast.success('Inquiry marked as completed');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as completed');
    }
  };

  // Helper to get status badge
  const getStatusBadge = (status, vendorAccepted, customerAccepted) => {
    // Visual polish (Feb 2026): every pill ships with a matching 1px
    // border so the status reads as an intentional chip instead of a
    // flat color block. Same pattern used by the Customer Events list,
    // so both operator surfaces share one visual language.
    const statusConfig = {
      'pending': { color: 'bg-amber-100 text-amber-800 border border-amber-200', label: 'Awaiting Your Response' },
      'reviewing': { color: 'bg-cyan-100 text-cyan-800 border border-cyan-200', label: 'In Discussion' },
      'proposal_sent': { color: 'bg-purple-100 text-purple-800 border border-purple-200', label: 'Proposal Sent' },
      'proposal_rejected': { color: 'bg-red-100 text-red-800 border border-red-200', label: 'Proposal Rejected' },
      'accepted': { color: 'bg-emerald-100 text-emerald-800 border border-emerald-200', label: 'Confirmed' },
      'in_progress': { color: 'bg-indigo-100 text-indigo-800 border border-indigo-200', label: 'In Progress' },
      'on_hold': { color: 'bg-yellow-100 text-yellow-800 border border-yellow-200', label: 'On Hold' },
      'completed': { color: 'bg-green-100 text-green-800 border border-green-200', label: 'Completed' },
      'cancelled': { color: 'bg-red-100 text-red-800 border border-red-200', label: 'Cancelled' },
      'declined': { color: 'bg-gray-100 text-gray-800 border border-gray-200', label: 'Declined' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Compute publish-readiness from current profileData
  const getPublishReadiness = () => {
    const checks = [
      { label: 'Business Name', ok: !!profileData.business_name?.trim(), section: 'basic-info' },
      { label: 'Contact Person', ok: !!profileData.contact_person?.trim(), section: 'basic-info' },
      { label: 'Service Area (State)', ok: !!profileData.state, section: 'basic-info' },
      { label: 'Service Area (ZIP)', ok: !!profileData.zip_code?.trim(), section: 'basic-info' },
      { label: 'Profile Photo', ok: !!profileData.profile_photo, section: 'photos' },
      { label: 'Email', ok: !!profileData.email?.trim(), section: 'preferences' },
      { label: 'Phone', ok: !!profileData.phone?.trim(), section: 'preferences' },
      { label: 'At least 1 Category', ok: profileData.categories?.length > 0, section: 'services' },
    ];
    const missing = checks.filter(c => !c.ok);
    return { isReady: missing.length === 0, checks, missing };
  };

  const publishReadiness = getPublishReadiness();

  // ───────── Save-path hardening (Track 1) ─────────
  // Shared pre-save validation + payload builder. Both `handleProfileSubmit`
  // and `handleGoLive` use this to stay in lockstep.
  //
  // Returns:
  //   { ok: true,  payload }  → safe to PATCH
  //   { ok: false }           → caller must abort save (toast already shown)
  const buildCleanedProfilePayload = () => {
    const services = Array.isArray(profileData.services) ? profileData.services : [];

    // Block save when a service row has content in price/description/duration/
    // includes but no name. Silently dropping such a row during filter was
    // the onboarding-blocker from the audit.
    const hasContentButNoName = (s) => {
      if (!s || typeof s !== 'object') return false;
      const nameMissing = !s.name || !String(s.name).trim();
      if (!nameMissing) return false;
      const otherPopulated = (
        (typeof s.starting_price === 'string' && s.starting_price.trim() !== '') ||
        (typeof s.description === 'string' && s.description.trim() !== '') ||
        (typeof s.duration === 'string' && s.duration.trim() !== '') ||
        (Array.isArray(s.includes) && s.includes.some(i => i && String(i).trim() !== ''))
      );
      return otherPopulated;
    };
    if (services.some(hasContentButNoName)) {
      toast.error('Please add a name to your package before saving');
      return { ok: false };
    }

    const cleanedData = {
      ...profileData,
      // `|| []` guard: protects against legacy docs where portfolio_photos is null
      portfolio_photos: (profileData.portfolio_photos || []).filter(p => p && p.trim() !== ''),
      services: services.filter(s => s.name && s.name.trim() !== ''),
      specialties: (profileData.specialties || []).filter(s => s && s.trim() !== ''),
      // Coerce to number; empty / NaN falls back to the initial default (25)
      service_radius: Number(profileData.service_radius) || 25,
    };

    return { ok: true, payload: cleanedData };
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    const built = buildCleanedProfilePayload();
    if (!built.ok) return; // toast already shown; do NOT hit PATCH

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Use draft save endpoint — accepts partial data
      await axios.patch(`${API}/vendors/me/draft`, built.payload, { headers });
      toast.success(profileVisibility?.vendor_active ? 'Profile updated!' : 'Draft saved!');

      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchData();
      // Keep canonical onboarding phase + missing list in lockstep with the
      // DB. Without this the lower "Ready to publish?" checklist can show
      // stale field-missing labels right after a successful save.
      try { refreshOnboarding && refreshOnboarding(); } catch (_) { /* noop */ }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const fields = detail.map(d => d.loc?.[d.loc.length - 1]).filter(Boolean).join(', ');
        toast.error(`Validation error: ${fields || 'Check required fields'}`);
      } else {
        toast.error(detail || 'An error occurred while saving your profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoLive = async () => {
    // Frontend check first
    if (!publishReadiness.isReady) {
      const firstMissing = publishReadiness.missing[0];
      toast.error(`Complete required fields first: ${publishReadiness.missing.map(m => m.label).join(', ')}`);
      setActiveProfileSection(firstMissing.section);
      setTimeout(() => {
        const section = document.querySelector(`[data-testid="section-${firstMissing.section}"]`);
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return;
    }

    const built = buildCleanedProfilePayload();
    if (!built.ok) return; // toast already shown; do NOT hit PATCH

    // Save draft first, then go live
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Save draft first
      await axios.patch(`${API}/vendors/me/draft`, built.payload, { headers });
      
      // Then activate
      const response = await axios.patch(`${API}/vendors/me/visibility`, { vendor_active: true }, { headers });
      setProfileVisibility({
        vendor_active: response.data.vendor_active,
        admin_visible: response.data.admin_visible,
        is_visible_to_customers: response.data.is_visible_to_customers,
        admin_blocked: !response.data.admin_visible
      });
      refreshOnboarding();
      toast.success('Profile is now live! Customers can find you.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured incomplete_onboarding error from backend publish gate.
      if (detail && typeof detail === 'object' && detail.error === 'incomplete_onboarding') {
        const missing = detail.missing || [];
        // Targeted UX handoff: if the ONLY remaining blocker is the
        // Platform Fee Agreement, open the agreement dialog directly
        // instead of showing a red error toast. Vendors should not have
        // to hunt for the ambient banner. The dialog remains dismissible
        // — accepting is still optional / postponable.
        const isOnlyAgreementMissing =
          missing.length === 1 && missing[0] === 'Accept the Platform Agreement';
        if (isOnlyAgreementMissing) {
          setAgreementIntent('onboarding');
          setPayoutAgreementChecked(false);
          setShowPayoutAgreement(true);
        } else {
          const missingJoined = missing.join(', ');
          toast.error(
            missingJoined
              ? `Profile not ready to publish. Missing: ${missingJoined}`
              : (detail.message || 'Profile not ready to publish')
          );
        }
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Could not publish profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePortfolioChange = (index, value) => {
    const newPhotos = [...profileData.portfolio_photos];
    newPhotos[index] = value;
    setProfileData({ ...profileData, portfolio_photos: newPhotos });
  };

  const addPortfolioField = () => {
    setProfileData({
      ...profileData,
      portfolio_photos: [...profileData.portfolio_photos, '']
    });
  };

  const removePortfolioField = (index) => {
    const newPhotos = profileData.portfolio_photos.filter((_, i) => i !== index);
    setProfileData({ ...profileData, portfolio_photos: newPhotos });
  };

  const toggleCategory = async (category) => {
    let newCategories;
    setProfileData(prev => {
      const isRemoving = prev.categories.includes(category);
      newCategories = isRemoving
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      
      let newPrimary = prev.primary_service;
      if (!newCategories.includes(newPrimary)) {
        newPrimary = newCategories.length === 1 ? newCategories[0] : '';
      }
      if (newCategories.length === 1) {
        newPrimary = newCategories[0];
      }
      
      return { ...prev, categories: newCategories, primary_service: newPrimary };
    });

    // Auto-apply onboarding template for NEW vendors selecting their first category
    const isAdding = !profileData.categories.includes(category);
    const wouldBeFirst = profileData.categories.length === 0;
    if (!vendorProfile && !templateApplied && isAdding && wouldBeFirst) {
      try {
        const res = await axios.get(`${API}/vendor/onboarding-template`, { params: { category } });
        const tpl = res.data;
        setProfileData(prev => ({
          ...prev,
          short_tagline: prev.short_tagline || tpl.short_tagline || '',
          bio: prev.bio || tpl.bio || '',
          starting_price: (prev.starting_price && prev.starting_price.length > 0) ? prev.starting_price : (tpl.starting_price || []),
          services: (prev.services && prev.services.length > 0) ? prev.services : (tpl.services || []),
        }));
        setTemplateApplied(true);
        // Phase-5-alignment: differentiate category-specific templates from
        // the neutral generic fallback. When `template_key === 'generic'`
        // we silently seed neutral examples — the profile fields populate
        // visibly so no toast is needed. For category-specific templates
        // we surface a short success toast so the vendor knows we tailored
        // their starter content.
        const isGeneric = tpl.template_key === 'generic';
        if (!isGeneric) {
          toast.success("We've created a starter profile based on your service. Customize it anytime!");
        }
      } catch (err) {
        console.log('Could not fetch onboarding template:', err);
      }
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDateSelect = (dates) => {
    // Calendar in multiple mode returns an array of dates
    if (!dates) {
      setBlockedDates([]);
      return;
    }
    
    // Ensure dates is an array
    const dateArray = Array.isArray(dates) ? dates : [dates];
    setBlockedDates(dateArray);
  };

  const removeBlockedDate = (dateToRemove) => {
    const dateStr = dateToRemove.toISOString().split('T')[0];
    const newBlockedDates = blockedDates.filter(d => {
      const dStr = d.toISOString().split('T')[0];
      return dStr !== dateStr;
    });
    setBlockedDates(newBlockedDates);
  };

  const saveBlockedDates = async () => {
    if (!vendorProfile) {
      toast.error('Please create your service provider profile first');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const dateStrings = blockedDates.map(d => d.toISOString().split('T')[0]);
      
      await axios.patch(
        `${API}/vendors/${vendorProfile.id}/blocked-dates`,
        { blocked_dates: dateStrings },
        { headers }
      );
      
      toast.success('Availability calendar updated successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update calendar');
    } finally {
      setLoading(false);
    }
  };

  const saveBookingRules = async () => {
    if (!vendorProfile) {
      toast.error('Please create your service provider profile first');
      return;
    }

    setSavingBookingRules(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.patch(
        `${API}/vendors/${vendorProfile.id}/booking-rules`,
        {
          allow_same_day: bookingRules.allow_same_day,
          same_day_cutoff_hour: bookingRules.same_day_cutoff_hour,
          same_day_cutoff_minute: bookingRules.same_day_cutoff_minute,
          allow_next_day: bookingRules.allow_next_day,
          min_notice_hours: bookingRules.min_notice_hours,
          full_payment_discount_enabled: bookingRules.full_payment_discount_enabled,
          full_payment_discount_percent: bookingRules.full_payment_discount_percent,
          deposit_window_hours: bookingRules.deposit_window_hours,
          allow_deposit_extensions: bookingRules.allow_deposit_extensions
        },
        { headers }
      );
      
      toast.success('Booking rules updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking rules');
    } finally {
      setSavingBookingRules(false);
    }
  };

  // Convert min_notice_hours to days for UI display
  const minNoticeDays = Math.floor(bookingRules.min_notice_hours / 24);
  
  const handleMinNoticeDaysChange = (days) => {
    setBookingRules(prev => ({
      ...prev,
      min_notice_hours: days * 24
    }));
  };

  // Format cutoff time for display (12-hour format)
  const formatCutoffTime = () => {
    const hour = bookingRules.same_day_cutoff_hour;
    const minute = bookingRules.same_day_cutoff_minute;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (upfront — keeps non-image selections like PDFs out).
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
      toast.error('Please upload an image file (JPEG, PNG, HEIC, or WebP)');
      return;
    }

    setUploadingImage(true);
    try {
      // Shared normalizer: decodes (HEIC OK on modern iOS), resizes to 1024px
      // longest edge, and re-encodes JPEG at quality 0.9 → 0.6 until ≤ 5 MB.
      const { file: normalized } = await normalizeImageToFile(file, { preset: 'profile' });

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', normalized);

      const response = await axios.post(`${API}/upload/profile?as=vendor`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setProfileData({ ...profileData, profile_photo: response.data.url });
      toast.success('Profile image uploaded!');
      // Backend persists `profile_photo` to the vendor doc inside the upload
      // endpoint now, so refresh the canonical onboarding phase so the
      // "Profile Photo" check ticks immediately without requiring Save Draft.
      try { refreshOnboarding && refreshOnboarding(); } catch (_) { /* noop */ }
      // Notify the Navigation header avatar to refetch immediately so the
      // uploaded photo appears without requiring a route change.
      try {
        window.dispatchEvent(new CustomEvent('celey:profile-photo-updated'));
      } catch (_) { /* CustomEvent unsupported — header will refresh on next nav */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePortfolioImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
      toast.error('Please upload an image file (JPEG, PNG, HEIC, or WebP)');
      return;
    }

    setUploadingImage(true);
    try {
      // Portfolio images keep more resolution (2048px longest edge).
      const { file: normalized } = await normalizeImageToFile(file, { preset: 'portfolio' });

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', normalized);

      const response = await axios.post(`${API}/upload/portfolio`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newPhotos = [...profileData.portfolio_photos];
      newPhotos[index] = response.data.url;
      setProfileData({ ...profileData, portfolio_photos: newPhotos });
      toast.success('Portfolio image uploaded!');
      // Backend appends to vendor.portfolio_photos. Local state owns slot
      // ordering; refresh the canonical phase so any storefront-strip
      // indicators tied to portfolio_photos pick up the new count.
      try { refreshOnboarding && refreshOnboarding(); } catch (_) { /* noop */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeProfilePhoto = () => {
    setProfileData({ ...profileData, profile_photo: '' });
    toast.success('Profile photo removed. Upload a new one or save without it.');
  };

  const removePortfolioPhoto = (index) => {
    const oldUrl = profileData.portfolio_photos?.[index] || '';
    const newPhotos = [...profileData.portfolio_photos];
    newPhotos[index] = '';
    setProfileData(prev => {
      const next = { ...prev, portfolio_photos: newPhotos };
      // Delete-time reconciliation: if this URL was the hero, clear it.
      if (oldUrl && prev.cover_image === oldUrl) {
        next.cover_image = '';
      }
      return next;
    });
    toast.success('Portfolio photo removed');
  };

  // Hero selection: toggle-off if already featured, else set. Only URL values
  // present in portfolio_photos are eligible — enforced by the UI.
  const handleSetHero = (url) => {
    if (!url || typeof url !== 'string' || !url.trim()) return;
    setProfileData(prev => {
      if (prev.cover_image === url) {
        toast.success('Hero photo cleared');
        return { ...prev, cover_image: '' };
      }
      toast.success('Hero photo set');
      return { ...prev, cover_image: url };
    });
  };

  // Track E recovery: apply starter template for vendors whose services are
  // empty. Backend is idempotent and won't overwrite non-empty values.
  // Phase 4 fix: auto-save any pending profile edits first, so the template
  // merges with what the user sees on screen — not stale DB state.
  const handleApplyTemplate = async () => {
    if (applyingTemplate) return;
    setApplyingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Auto-save current profile draft first (silently). We use the same
      // payload builder used by the Save Draft button; if payload is invalid,
      // the builder surfaces its own toast and we stop here.
      const built = buildCleanedProfilePayload();
      if (!built.ok) return;
      try {
        await axios.patch(`${API}/vendors/me/draft`, built.payload, { headers });
      } catch (saveErr) {
        toast.error('Could not save your draft before applying the starter profile. Please try again.');
        return;
      }

      const res = await axios.post(`${API}/vendors/me/apply-template`, {}, { headers });
      if (res.data?.status === 'noop') {
        toast.info('Your profile already has content — nothing to apply.');
      } else {
        const fields = res.data?.applied_fields || [];
        // Single success path — message kept honest for the generic fallback.
        const note = res.data?.template_key === 'generic'
          ? `Neutral starter applied (${fields.length} field${fields.length === 1 ? '' : 's'}). Please review and customize.`
          : `Starter profile applied (${fields.length} field${fields.length === 1 ? '' : 's'}). Review and customize anytime.`;
        toast.success(note);
      }
      fetchData();
      refreshOnboarding();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not apply starter profile');
    } finally {
      setApplyingTemplate(false);
    }
  };

  return (
    <div className="min-h-screen-safe bg-gray-50 pb-20 md:pb-0 flex flex-col">
      <Navigation showFeedback={true} hideMobileNav={true} />

      {/* Responsive container - wider on tablets/desktops */}
      <div className="w-full max-w-screen-2xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-3 sm:py-4 md:py-8 flex-1">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          {/* Tab Navigation - Hidden on mobile (using bottom tab bar), visible on desktop */}
          <div className="relative hidden md:block">
            <div className="overflow-x-auto scrollbar-hide">
              <TabsList className="inline-flex min-w-full sm:min-w-0 sm:w-full justify-start sm:justify-center bg-gray-100/80 p-1 rounded-lg">
              <TabsTrigger value="welcome" data-testid="tab-welcome" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Home className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Welcome</span>
                <span className="sm:hidden">Home</span>
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">My Profile</span>
                <span className="sm:hidden">Profile</span>
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Schedule
              </TabsTrigger>
              <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Inbox className="w-3.5 h-3.5" />
                Requests
                {(() => {
                  // Badge reflects inquiries with UNREAD MESSAGES — the honest signal.
                  // Phase-1 fix: closed inquiries (declined/cancelled/completed) are
                  // excluded so a leftover unread on a closed thread no longer keeps
                  // the badge alive on the tab itself.
                  const unreadConvos = inquiries.filter(inq => (inq.unread_count || 0) > 0 && !isInquiryClosed(inq)).length;
                  return unreadConvos > 0 && <Badge className="ml-0.5 bg-emerald-500 text-[10px] px-1.5 py-0" data-testid="tab-inquiries-badge">{unreadConvos}</Badge>;
                })()}
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="tab-payments" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" />
                Payments
              </TabsTrigger>
              <TabsTrigger value="account" data-testid="tab-account" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="tips" data-testid="tab-tips" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5" />
                Tips
              </TabsTrigger>
            </TabsList>
            </div>
          </div>
          
          {/* Payment account problem alert — Payments tab only.
              Standard "incomplete" state is fully covered by the global sticky
              banner; this block now renders ONLY for the test-account / problem
              state where the sticky's friendly copy would actively mislead.
              Never rendered on Requests/Inquiries. */}
          {onboardingStatus &&
           !onboardingStatus.stripe_connected &&
           (onboardingStatus.participation_agreement_accepted || onboardingStatus.onboarding_completed) &&
           activeTab === 'payments' &&
           onboardingStatus.stripe_is_test_account && (
              <div className="rounded-lg p-3 flex items-center justify-between bg-red-50 border border-red-200" data-testid="payment-account-issue-alert">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-600">🚫</span>
                  <p className="text-xs font-medium text-red-800">
                    Payment account issue
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={handleStripeConnect}
                  disabled={stripeConnecting}
                  className="text-xs bg-red-600 hover:bg-red-700 text-white"
                >
                  {stripeConnecting ? '...' : 'Set Up'}
                </Button>
              </div>
          )}
          

          {/* Success Tips Banner - Only on Dashboard and Profile tabs */}
          {['profile'].includes(activeTab) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-emerald-600 mr-3">💡</span>
                <div>
                  <p className="text-sm font-medium text-emerald-800">Want more customers & better reviews?</p>
                  <p className="text-xs text-emerald-600">Check out our success tips to boost your visibility and bookings</p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('tips')}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-2"
              >
                <Lightbulb className="w-4 h-4" />
                View Tips
              </button>
            </div>
          )}



          {/* Welcome Tab — Clean Dashboard Layout (cleanup pass) */}
          <TabsContent value="welcome">
            {/*
              PRIMARY FOCUS SLOT — this tab renders ONE Primary Focus card
              (currently OnboardingGuideCard). Declaring focus here suppresses
              the global OnboardingStrip on this surface only, so the user
              sees a single hero signal instead of two competing ones. The
              strip continues to render on every other tab and route.

              Future Primary Focus cards (e.g. "Respond to N requests")
              should be slotted in below the OnboardingGuideCard or replace
              it via priority — the suppression mechanism is already generic.
            */}
            <DeclarePrimaryFocus />
            <div className="space-y-6">

              {/* ── 1. GREETING + 2. FEE STATUS PILL ──────────── */}
              {(() => {
                const pendingInquiries = inquiries.filter(i => i.status === 'pending');
                const { score, isComplete, missing } = getOnboardingCompletion();
                const needsStripe = onboardingStatus && !onboardingStatus.stripe_connected && (onboardingStatus.participation_agreement_accepted || onboardingStatus.onboarding_completed);
                const needsOnboarding = onboardingStatus && !onboardingStatus.participation_agreement_accepted && !onboardingStatus.onboarding_completed;
                const hasActions = pendingInquiries.length > 0 || needsStripe || needsOnboarding;

                const hour = new Date().getHours();
                const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

                // ── Deterministic, state-aware hero subtext ladder ──
                // Reuses the same lifecycle classifier + state derivations
                // the dashboard tile + chip systems already consume so the
                // subtext can never disagree with the visible counts.
                // Priority order is strict — first match wins, no rotation,
                // no randomness, no AI.
                const stateOf = (i) => getInquiryLifecycleState(i);
                const needsResponseCount = inquiries.filter(i => stateOf(i) === 'needs_response').length;
                const inProgressCount = inquiries.filter(i => stateOf(i) === 'in_progress').length;
                const bookedCount = inquiries.filter(i => stateOf(i) === 'booked').length;
                const unreadCount = inquiries.filter(i => (i.unread_count || 0) > 0 && !['canceled', 'completed'].includes(stateOf(i))).length;
                const paymentsCount = inquiries.filter(i => {
                  const s = stateOf(i);
                  if (['canceled', 'completed', 'ready_to_close'].includes(s)) return false;
                  const bk = getBookingForInquiry(i.id);
                  const ed = bk?.event_date || i.event_date;
                  if (ed) {
                    const d = new Date(ed);
                    d.setHours(23, 59, 59, 999);
                    if (d < new Date()) return false;
                  }
                  return getPaymentStatus(i, bk).show_in_payments_tab;
                }).length;

                let heroSubtext;
                // P1 — urgent operational
                if (paymentsCount > 0) heroSubtext = 'You have payment activity requiring attention';
                else if (needsResponseCount > 0) heroSubtext = 'You have requests waiting for a response';
                else if (unreadCount > 0) heroSubtext = 'You have unread messages from customers';
                // P2 — active operational
                else if (inProgressCount > 0) heroSubtext = 'You have active work in progress';
                else if (bookedCount > 0) heroSubtext = 'You have upcoming bookings to manage';
                // P3 — growth / caught-up
                else if (!isComplete) heroSubtext = 'Keep your business profile fresh';
                else if (inquiries.length > 0) heroSubtext = "You\u2019re all caught up";
                else heroSubtext = 'Grow your visibility and bookings';

                return (
                  <DashboardHero
                    testid="dashboard-header-section"
                    greeting={`${greeting}${vendorProfile?.business_name ? `, ${vendorProfile.business_name}` : ''}`}
                    subtitle={heroSubtext}
                  />
                );
              })()}

              {/* ── 2.5 ONBOARDING GUIDE CARD (Phase 4.5 anchor) ──
                  Single, anchored guidance signal. Renders for every
                  pre-live phase (via the canonical ONBOARDING_CTA_MAP).
                  Delegates CTA resolution to the shared phase-to-action
                  handler so card and ambient strip stay in sync. */}
              <OnboardingGuideCard
                onCtaClick={({ tab, type }) => {
                  if (type === 'agreement'
                      && onboardingStatus
                      && !onboardingStatus.participation_agreement_accepted) {
                    // Fresh vendor — open the real first blocker directly.
                    setAgreementIntent('onboarding');
                    setPayoutAgreementChecked(false);
                    setShowPayoutAgreement(true);
                    return;
                  }
                  setActiveTab(tab === 'payments' ? 'payments' : 'profile');
                }}
              />

              {/* ── 3. NEXT STEPS (onboarding signals moved to OnboardingStrip — Phase 4) ── */}
              {(() => {
                const pendingInquiries = inquiries.filter(i => i.status === 'pending');
                const { isComplete } = getOnboardingCompletion();

                // Phase 4 consolidation: onboarding-related Next Steps rows
                // ("Connect payouts", "Complete your profile") have been removed
                // because the global OnboardingStrip is now the single source of
                // truth for those signals. Next Steps is reserved for work the
                // vendor has in flight (pending requests, etc.).
                const steps = [];
                if (pendingInquiries.length > 0) {
                  steps.push({
                    key: 'requests',
                    icon: Inbox,
                    iconBg: 'bg-amber-100', iconColor: 'text-amber-700',
                    label: <>Respond to <span className="font-semibold">{pendingInquiries.length} {pendingInquiries.length > 1 ? 'requests' : 'request'}</span></>,
                    cta: 'Review',
                    onClick: () => setActiveTab('inquiries'),
                    testid: 'priority-new-inquiry',
                  });
                }

                // Soft-action vendor Next Steps from the backend (e.g. revise
                // rejected proposals). Always rendered AFTER urgent items, never
                // counted toward Needs Response, never urgency-styled.
                vendorNextSteps.forEach((s) => {
                  if (s.action_type === 'vendor_revise_proposal') {
                    const inqId = s.context?.inquiry_id;
                    const customerName = s.metadata?.customer_name;
                    steps.push({
                      key: s.step_key,
                      icon: MessageSquare,
                      iconBg: 'bg-gray-100', iconColor: 'text-gray-600',
                      label: customerName
                        ? <>Revise proposal for <span className="font-semibold">{customerName}</span></>
                        : 'Revise your proposal',
                      cta: s.metadata?.cta_label || 'Revise Proposal',
                      onClick: () => {
                        // Switch tab first so the chat sheet has visual context.
                        setActiveTab('inquiries');
                        // Reuse the existing per-row primitive to open chat.
                        const match = inquiries.find((x) => x.id === inqId);
                        if (match) {
                          handleOpenMessaging(match);
                        }
                      },
                      testid: `vendor-soft-step-${inqId || 'unknown'}`,
                    });
                  }
                });

                if (steps.length === 0 && isComplete) {
                  // Caught-up — light touch, no "You're all caught up" heavy card needed.
                  return null;
                }

                if (steps.length === 0) {
                  // Not fully complete but no urgent next step — handled by Grow section's profile card.
                  return null;
                }

                return (
                  <div data-testid="next-steps-section">
                    <SectionLabel>Next Steps</SectionLabel>
                    <div className="space-y-1.5">
                      {steps.map((s, idx) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={s.onClick}
                          disabled={s.disabled}
                          data-testid={s.testid}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all active:scale-[0.99] text-left ${
                            idx === 0
                              ? 'bg-white border border-gray-200 shadow-sm hover:bg-gray-50'
                              : 'bg-white border border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`h-7 w-7 rounded-full ${s.iconBg} flex items-center justify-center shrink-0`}>
                            <s.icon className={`h-3.5 w-3.5 ${s.iconColor}`} />
                          </div>
                          <p className={`flex-1 text-sm ${idx === 0 ? 'font-medium text-gray-900' : 'text-gray-800'}`}>{s.label}</p>
                          <span className="text-xs font-medium text-gray-400 shrink-0">{s.cta} &rarr;</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── 4. YOUR BUSINESS (informational stats) ───── */}
              {(() => {
                const stateOf = (i) => getInquiryLifecycleState(i);
                const needsResponse = inquiries.filter(i => stateOf(i) === 'needs_response').length;
                const inProgress = inquiries.filter(i => stateOf(i) === 'in_progress').length;
                const booked = inquiries.filter(i => stateOf(i) === 'booked').length;
                const paymentsNeedAttention = inquiries.filter(i => {
                  const s = stateOf(i);
                  if (['canceled', 'completed', 'ready_to_close'].includes(s)) return false;
                  const bk = getBookingForInquiry(i.id);
                  const ed = bk?.event_date || i.event_date;
                  if (ed) {
                    const d = new Date(ed);
                    d.setHours(23, 59, 59, 999);
                    if (d < new Date()) return false;
                  }
                  return getPaymentStatus(i, bk).show_in_payments_tab;
                }).length;
                const hasActivity = inquiries.length > 0;

                // Cleanup: stats are INFORMATIONAL, not alert-styled.
                // Numbers remain colored when non-zero (keeps visual scan);
                // backgrounds are uniform neutral (no bg-amber-50 / bg-red-50).
                return (
                  <div data-testid="performance-snapshot-section">
                    <SectionLabel>Your Business</SectionLabel>
                    {hasActivity ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                        <StatTile
                          icon={MessageSquare}
                          tone="amber"
                          value={needsResponse}
                          label="Needs Response"
                          active={needsResponse > 0}
                          onClick={() => { setRequestsFilter('needs_response'); setActiveTab('inquiries'); }}
                          testid="stat-needs-response"
                        />
                        <StatTile
                          icon={Clock}
                          tone="gold"
                          value={inProgress}
                          label="In Progress"
                          onClick={() => { setRequestsFilter('in_progress'); setActiveTab('inquiries'); }}
                          testid="stat-in-progress"
                        />
                        <StatTile
                          icon={CheckCircle}
                          tone="emerald"
                          value={booked}
                          label="Booked"
                          active={booked > 0}
                          onClick={() => { setRequestsFilter('booked'); setActiveTab('inquiries'); }}
                          testid="stat-booked"
                        />
                        <StatTile
                          icon={Wallet}
                          tone="rose"
                          value={paymentsNeedAttention}
                          label="Payments"
                          sublabel={paymentsNeedAttention > 0 ? 'Action needed' : null}
                          sublabelTone="rose"
                          active={paymentsNeedAttention > 0}
                          onClick={() => { setRequestsFilter('payments'); setActiveTab('inquiries'); }}
                          testid="stat-payments-attention"
                        />
                      </div>
                    ) : (
                      <div className="p-4 rounded-2xl bg-white border border-gray-100/80 shadow-tile text-center">
                        <p className="text-sm text-gray-400">Your business stats will appear once you get your first request</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── 5. QUICK ACTIONS (preserved, low-emphasis) ─ */}
              <div data-testid="quick-actions-section">
                <SectionLabel>Quick Actions</SectionLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <StatTile
                    icon={Inbox}
                    tone="emerald"
                    label="View Requests"
                    onClick={() => setActiveTab('inquiries')}
                    testid="quick-action-inquiries"
                  />
                  <StatTile
                    icon={User}
                    tone="blue"
                    label="Edit Profile"
                    onClick={() => setActiveTab('profile')}
                    testid="quick-action-profile"
                  />
                  <StatTile
                    icon={Layers}
                    tone="purple"
                    label="Manage Services"
                    onClick={() => { setProfileDefaultSection('services'); setActiveTab('profile'); }}
                    testid="quick-action-services"
                  />
                  <StatTile
                    icon={Calendar}
                    tone="amber"
                    label="Manage Schedule"
                    onClick={() => setActiveTab('calendar')}
                    testid="quick-action-calendar"
                  />
                </div>
              </div>

              {/* ── 6. GROW YOUR BUSINESS (referrals + tips only) ──────────── */}
              {/* Phase 4: profile-completion progress card moved off Welcome
                  tab. The OnboardingStrip owns onboarding signaling globally.
                  Profile-strength breakdown remains available inside the
                  Profile tab's "Ready to publish?" checklist for detailed
                  inspection. */}
              <div data-testid="grow-business-section">
                <SectionLabel>Grow Your Business</SectionLabel>
                <div className="space-y-2">
                  {/* Referral + tips tiles — Feb 2026 polish: aligned with
                      StatTile / Customer Events / Vendor Requests cards
                      (rounded-2xl, shadow-tile, tinted icon chip,
                      chevron affordance, active:scale-[0.99]). Copy,
                      routing, CTA destinations, testids preserved. */}
                  <div className="pt-1 space-y-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab('account-referrals')}
                      className="w-full p-3.5 bg-white rounded-2xl border border-gray-100/80 shadow-tile hover:shadow-card hover:border-gray-200 transition-all duration-150 active:scale-[0.99] flex items-center justify-between text-left"
                      data-testid="grow-referral"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                          <Share2 className="h-[18px] w-[18px]" />
                        </div>
                        <p className="text-sm text-gray-700">Know a great provider? Earn rewards for referrals</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 shrink-0 ml-2" />
                    </button>

                    <button
                      type="button"
                      onClick={() => { setActiveTab('tips'); setTimeout(() => document.getElementById('tips-profile')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300); }}
                      className="w-full p-3.5 bg-white rounded-2xl border border-gray-100/80 shadow-tile hover:shadow-card hover:border-gray-200 transition-all duration-150 active:scale-[0.99] flex items-center justify-between text-left"
                      data-testid="grow-tip"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                          <Lightbulb className="h-[18px] w-[18px]" />
                        </div>
                        <p className="text-sm text-gray-700">Boost your visibility and bookings</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 shrink-0 ml-2" />
                    </button>
                  </div>
                </div>
              </div>

              {/* ── 7. UPDATES (hidden when empty) ───────────── */}
              {updateBanners.length > 0 && (
                <div data-testid="updates-section">
                  <SectionLabel>Updates</SectionLabel>
                  <div className="space-y-2">
                    {updateBanners.map(b => {
                      const typeStyles = {
                        critical: { bg: 'bg-red-50', border: 'border-red-100', title: 'text-red-700', msg: 'text-red-600', btn: 'bg-red-500 hover:bg-red-600 text-white', dismiss: 'text-red-300 hover:text-red-500' },
                        warning: { bg: 'bg-amber-50', border: 'border-amber-100', title: 'text-amber-700', msg: 'text-amber-600', btn: 'bg-amber-500 hover:bg-amber-600 text-white', dismiss: 'text-amber-300 hover:text-amber-500' },
                        success: { bg: 'bg-emerald-50', border: 'border-emerald-100', title: 'text-emerald-700', msg: 'text-emerald-600', btn: 'bg-emerald-500 hover:bg-emerald-600 text-white', dismiss: 'text-emerald-300 hover:text-emerald-500' },
                        info: { bg: 'bg-blue-50', border: 'border-blue-100', title: 'text-blue-700', msg: 'text-blue-600', btn: 'bg-blue-500 hover:bg-blue-600 text-white', dismiss: 'text-blue-300 hover:text-blue-500' },
                      };
                      const s = typeStyles[b.type] || typeStyles.info;
                      return (
                        <div key={b.id} className={`p-3 rounded-lg border ${s.bg} ${s.border}`} data-testid={`update-card-${b.id}`}>
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              {b.title && <p className={`text-sm font-medium ${s.title}`}>{b.title}</p>}
                              <p className={`text-xs ${b.title ? 'mt-0.5' : ''} ${s.msg}`}>{b.message}</p>
                              {b.cta_text && b.cta_link && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (b.cta_link.startsWith('http')) { openExternalUrl(b.cta_link); }
                                    else { navigate(b.cta_link); }
                                  }}
                                  className={`mt-2 text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${s.btn}`}
                                >
                                  {b.cta_text}
                                  {b.cta_link.startsWith('http') && <ExternalLink className="h-3 w-3" />}
                                </button>
                              )}
                            </div>
                            {b.dismissible !== false && (
                              <button type="button" onClick={() => dismissBannerCard(b.id)} className={`p-0.5 ${s.dismiss}`}>
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Floating banners removed — all announcements live in Updates section above */}
            </div>
          </TabsContent>

          <TabsContent value="profile">
            {/* Stripe Connection Status Card - ONLY shows when Stripe IS FULLY connected */}
            {/* stripe_connected is now true ONLY when charges_enabled AND payouts_enabled are both true */}
            {vendorProfile && onboardingStatus && onboardingStatus.stripe_connected && onboardingStatus.stripe_onboarding_complete && (
              <div className="mb-4 flex items-center gap-3 bg-emerald-50/60 rounded-xl border border-emerald-100 p-3.5">
                <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-emerald-800">Payment Account Connected</p>
                  <p className="text-[11px] text-emerald-600">You can receive payments from customers</p>
                </div>
              </div>
            )}

            {/* Starter profile template notice — shown only for new vendors after template auto-fill */}
            {!vendorProfile && templateApplied && (
              <div className="mb-4 flex items-center gap-3 bg-blue-50/60 rounded-xl border border-blue-100 p-3.5" data-testid="template-applied-banner">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-blue-800">Starter profile created</p>
                  <p className="text-[11px] text-blue-600">We pre-filled your services, tagline, and description based on your category. Customize it anytime.</p>
                </div>
              </div>
            )}

            {/* Track E recovery CTA — for existing vendors with categories but no services */}
            {vendorProfile && (profileData.categories || []).length > 0 && (profileData.services || []).length === 0 && !applyingTemplate && (
              <div className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center bg-emerald-50/70 rounded-xl border border-emerald-200 p-3.5" data-testid="apply-template-cta">
                <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-emerald-900">Apply starter profile for {profileData.categories?.[0]}</p>
                  <p className="text-[11px] text-emerald-700">We'll pre-fill 3 starter services, a tagline, and a bio — all fully editable.</p>
                </div>
                <Button
                  size="sm"
                  onClick={handleApplyTemplate}
                  fullWidth="mobile"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 rounded-full sm:shrink-0"
                  data-testid="apply-template-btn"
                >
                  Apply starter profile
                </Button>
              </div>
            )}

            <VendorProfileForm
              vendorProfile={vendorProfile}
              profileData={profileData}
              setProfileData={setProfileData}
              loading={loading}
              user={user}
              uploadingImage={uploadingImage}
              categorySearch={categorySearch}
              setCategorySearch={setCategorySearch}
              categories={categories}
              states={states}
              priceRanges={priceRanges}
              getImageUrl={getImageUrl}
              handleProfileSubmit={handleProfileSubmit}
              handleGoLive={handleGoLive}
              publishReadiness={publishReadiness}
              isLive={profileVisibility?.vendor_active || false}
              handleChange={handleChange}
              handleProfileImageUpload={handleProfileImageUpload}
              handlePortfolioImageUpload={handlePortfolioImageUpload}
              removeProfilePhoto={removeProfilePhoto}
              removePortfolioPhoto={removePortfolioPhoto}
              handleSetHero={handleSetHero}
              addPortfolioField={addPortfolioField}
              removePortfolioField={removePortfolioField}
              toggleCategory={toggleCategory}
              profileLinkCopied={profileLinkCopied}
              setProfileLinkCopied={setProfileLinkCopied}
              defaultSection={profileDefaultSection}
              activeSection={activeProfileSection}
              setActiveSection={setActiveProfileSection}
              onSectionViewed={() => setProfileDefaultSection('basic-info')}
            />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            {/* Schedule header + segmented control */}
            <div className="mb-4 space-y-3" data-testid="schedule-header">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>
                <p className="text-xs text-gray-400 mt-0.5">Upcoming events, availability, and booking rules</p>
              </div>
              <div
                role="tablist"
                aria-label="Schedule sections"
                data-testid="schedule-segmented"
                className="inline-flex items-center bg-gray-100/80 rounded-full p-1"
              >
                {[
                  { key: 'upcoming', label: 'Upcoming' },
                  { key: 'calendar', label: 'Calendar' },
                  { key: 'rules',    label: 'Booking Rules' },
                ].map(seg => (
                  <button
                    key={seg.key}
                    type="button"
                    role="tab"
                    aria-selected={scheduleSegment === seg.key}
                    data-testid={`schedule-segment-${seg.key}`}
                    onClick={() => setScheduleSegment(seg.key)}
                    className={`text-[12px] font-medium px-3.5 py-1.5 rounded-full transition-colors ${
                      scheduleSegment === seg.key
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── UPCOMING SEGMENT (NEW) ───────────────────────── */}
            {scheduleSegment === 'upcoming' && (() => {
              // Upcoming = lifecycle state 'booked' with a future event date.
              // Consumes the same classifier as the Requests chips so the
              // two views can never disagree on what counts as "booked".
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const confirmed = inquiries
                .map(inq => ({ inq, bk: getBookingForInquiry(inq.id) }))
                .filter(({ inq, bk }) => {
                  if (getInquiryLifecycleState(inq) !== 'booked') return false;
                  const ed = bk?.event_date || inq.event_date;
                  if (!ed) return false;
                  return new Date(ed) >= today;
                })
                .sort((a, b) => {
                  const da = new Date(a.bk.event_date || a.inq.event_date);
                  const db = new Date(b.bk.event_date || b.inq.event_date);
                  return da - db;
                });

              if (confirmed.length === 0) {
                return (
                  <div data-testid="upcoming-empty" className="p-6 rounded-xl bg-gray-50/70 border border-gray-100 text-center">
                    <p className="text-sm text-gray-500 font-medium">No upcoming events</p>
                    <p className="text-[11px] text-gray-400 mt-1">Confirmed bookings with future event dates will appear here.</p>
                  </div>
                );
              }

              const daysUntil = (iso) => {
                const d = new Date(iso);
                d.setHours(0, 0, 0, 0);
                const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
                if (diff === 0) return 'Today';
                if (diff === 1) return 'Tomorrow';
                return `${diff} days away`;
              };

              const [next, ...rest] = confirmed;
              const renderRow = ({ inq, bk }, isNext) => {
                const eventDate = bk.event_date || inq.event_date;
                const dateObj = new Date(eventDate);
                const dateLabel = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const timeLabel = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                const customer = inq.customer_name || 'Customer';
                // Decision Reduction Engine context: prefer event type as the
                // headline, then the customer's chosen package. The legacy
                // `inq.service_type / inq.service / bk.service_name` chain was
                // a dead fallback (no such fields exist on the Inquiry or
                // Booking models) which always resolved to the literal string
                // "Service". Replaced with real fields only.
                const eventType = (inq.event_type || '').trim() || null;
                const packageName = (inq.selected_package || '').trim() || null;
                // When the customer did not click a specific package, fall
                // back to the vendor's own primary service (set during
                // onboarding). Never inferred from categories — only the
                // vendor's explicit lead offering is used.
                const primaryService = (profileData?.primary_service || '').trim() || null;
                const subtitleService = packageName || primaryService || null;
                const titleLabel = eventType || 'Upcoming Event';
                const subtitle1 = subtitleService
                  ? `${customer} · ${subtitleService}`
                  : customer;
                // Payment chip
                let paymentChip = null;
                if (bk.final_paid) {
                  paymentChip = <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 font-medium">Paid</span>;
                } else if (bk.final_payment_due_date && new Date(bk.final_payment_due_date) < today) {
                  paymentChip = <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5 font-medium">Payment Due</span>;
                } else if (bk.final_payment_due_date) {
                  const dueDate = new Date(bk.final_payment_due_date);
                  const daysTillDue = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
                  if (daysTillDue <= 7) {
                    paymentChip = <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5 font-medium">Payment Due Soon</span>;
                  } else {
                    paymentChip = <span className="text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5">Deposit paid</span>;
                  }
                } else {
                  paymentChip = <span className="text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5">Deposit paid</span>;
                }
                return (
                  <button
                    key={bk.id || inq.id}
                    type="button"
                    data-testid={`upcoming-row-${bk.id || inq.id}`}
                    onClick={() => {
                      // Schedule → Requests handoff. Do NOT auto-open chat
                      // (closing chat used to drop the vendor onto an
                      // unrelated Requests filter). Pick the chip that
                      // already shows this lifecycle state and signal the
                      // matching card to scroll + flash.
                      setRequestsFilter(getFilterForInquiry(inq));
                      setHighlightInquiryId(inq.id);
                      setActiveTab('inquiries');
                    }}
                    className={`w-full text-left p-3.5 rounded-xl bg-white border transition-colors hover:bg-gray-50 ${isNext ? 'border-gray-200 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className={`text-[14px] truncate ${isNext ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>{titleLabel}</p>
                        <p className="text-[12px] text-gray-500 mt-0.5 truncate">{subtitle1}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                          {dateLabel} · {timeLabel} · <span className="text-gray-400">{daysUntil(eventDate)}</span>{bk.event_location ? ` · ${bk.event_location}` : ''}
                        </p>
                      </div>
                      {paymentChip}
                    </div>
                  </button>
                );
              };

              return (
                <div data-testid="upcoming-section" className="space-y-4">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Next Up</p>
                    {renderRow(next, true)}
                  </div>
                  {rest.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Upcoming Events</p>
                      <div className="space-y-1.5">
                        {rest.map(item => renderRow(item, false))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── CALENDAR SEGMENT (EXISTING + dot overlay) ───── */}
            {scheduleSegment === 'calendar' && (
            <div data-testid="calendar-card" className="space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CalendarX className="h-4 w-4 text-gray-500" />
                  <h3 className="text-[15px] font-bold text-gray-900">Manage Availability</h3>
                </div>
                <p className="text-[12px] text-gray-400">Click dates to block them. Customers can't request blocked dates.</p>
              </div>

              {!vendorProfile ? (
                <div className="text-center py-10 text-[13px] text-gray-500" data-testid="no-profile-calendar">
                  Please create your service provider profile first to manage your availability.
                </div>
              ) : (
                <div className="space-y-4 w-full max-w-full sm:max-w-xl md:max-w-2xl mx-auto">
                  <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 md:p-6 w-full">
                    <CalendarComponent
                      mode="multiple"
                      selected={blockedDates}
                      onSelect={handleDateSelect}
                      onDayClick={(day) => {
                        // Day detail overlay: if the date is blocked or has bookings, show detail.
                        // Otherwise, the `onSelect` above already toggles blocking via handleDateSelect.
                        const isoDay = new Date(day).toISOString().slice(0, 10);
                        const hasBooking = inquiries.some(inq => {
                          const bk = getBookingForInquiry(inq.id);
                          if (!bk) return false;
                          if (['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(bk.status)) return false;
                          const ed = bk.event_date || inq.event_date;
                          return ed && new Date(ed).toISOString().slice(0, 10) === isoDay;
                        });
                        const isBlocked = (blockedDates || []).some(d => new Date(d).toISOString().slice(0, 10) === isoDay);
                        if (hasBooking || isBlocked) {
                          setScheduleDayDetail(isoDay);
                        }
                      }}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      modifiers={{
                        booked: inquiries
                          .map(inq => {
                            const bk = getBookingForInquiry(inq.id);
                            if (!bk) return null;
                            if (['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(bk.status)) return null;
                            const ed = bk.event_date || inq.event_date;
                            return ed ? new Date(ed) : null;
                          })
                          .filter(Boolean),
                      }}
                      classNames={{
                        // Ensure the calendar stretches to fill its wrapper on all breakpoints.
                        months: 'flex flex-col sm:flex-row w-full space-y-4 sm:space-x-4 sm:space-y-0',
                        month: 'w-full space-y-4',
                        table: 'w-full border-collapse',
                        // Normalize cell geometry — perfect squares, equal spacing, proper alignment on all breakpoints
                        head_row: 'grid grid-cols-7 w-full',
                        head_cell: 'text-gray-500 font-semibold text-[0.7rem] uppercase tracking-wider text-center py-1.5',
                        row: 'grid grid-cols-7 w-full',
                        cell: 'relative text-center text-sm focus-within:relative focus-within:z-20 aspect-square border-t border-l border-gray-100 first:border-l-0 [&:nth-child(7n)]:border-r-0',
                        day: 'relative h-full w-full aspect-square p-0 text-[13px] font-semibold text-gray-800 hover:bg-gray-50 aria-selected:opacity-100 transition-colors',
                        // Clear, non-conflicting state styling
                        day_today: 'ring-2 ring-inset ring-emerald-500 text-emerald-700',
                        day_selected: 'bg-red-500 text-white hover:bg-red-600 focus:bg-red-600',
                        day_outside: 'text-gray-300 font-normal',
                        day_disabled: 'text-gray-300 font-normal opacity-60',
                        day_hidden: 'invisible',
                      }}
                      modifiersClassNames={{
                        // Subtle booked indicator — positioned absolutely so it never affects cell geometry
                        booked: 'after:content-[""] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-emerald-500 after:pointer-events-none',
                      }}
                      className="rounded-lg w-full border border-gray-100 bg-white"
                      data-testid="availability-calendar"
                    />
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500 px-1">
                    <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Booked</span>
                    <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Blocked</span>
                    <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-gray-200" /> Available</span>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] font-semibold text-gray-700">Blocked Dates</span>
                      <span className="text-[11px] text-gray-400">{blockedDates.length} dates</span>
                    </div>
                    {blockedDates.length === 0 ? (
                      <p className="text-[12px] text-gray-400">No dates blocked yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {blockedDates
                          .sort((a, b) => a - b)
                          .map((date, idx) => (
                            <Badge 
                              key={idx} 
                              variant="destructive" 
                              className="cursor-pointer text-[10px] px-2 py-0.5 rounded-full"
                              onClick={() => removeBlockedDate(date)}
                              data-testid={`blocked-date-${idx}`}
                            >
                              {formatDate(date.toISOString())} <X className="h-2.5 w-2.5 ml-1" />
                            </Badge>
                          ))}
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    onClick={saveBlockedDates} 
                    disabled={loading} 
                    className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 h-10 text-[13px]"
                    data-testid="save-calendar-btn"
                  >
                    {loading ? 'Saving...' : 'Save Calendar'}
                  </Button>
                </div>
              )}
            </div>
            )}
            {/* END CALENDAR SEGMENT */}

            {/* ── BOOKING RULES SEGMENT (EXISTING, relocated) ── */}
            {scheduleSegment === 'rules' && vendorProfile && (
              <div data-testid="booking-rules-card">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <h3 className="text-[15px] font-bold text-gray-900">Booking Rules</h3>
                </div>
                <p className="text-[12px] text-gray-400 mb-4">Control advance notice, same-day availability, and payment terms.</p>
                <div className="space-y-5">
                  {/* ── 1. AVAILABILITY RULES ─────────────────── */}
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Availability</p>
                    <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-100">
                      {/* Same-day (inline toggle) */}
                      <div className="p-3.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-gray-900">Allow Same-Day Bookings</p>
                            <p className="text-[11px] text-gray-500">Let customers book services for today</p>
                          </div>
                          <Switch
                            checked={bookingRules.allow_same_day}
                            onCheckedChange={(checked) => setBookingRules(prev => ({ ...prev, allow_same_day: checked }))}
                            data-testid="same-day-toggle"
                          />
                        </div>
                        {bookingRules.allow_same_day && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                            <Label className="text-[11px] text-gray-600">Same-Day Cutoff Time (Eastern Time)</Label>
                            <div className="flex items-center gap-2">
                              <Select
                                value={String(bookingRules.same_day_cutoff_hour > 12 ? bookingRules.same_day_cutoff_hour - 12 : bookingRules.same_day_cutoff_hour === 0 ? 12 : bookingRules.same_day_cutoff_hour)}
                                onValueChange={(val) => {
                                  const hour = parseInt(val);
                                  const isPM = bookingRules.same_day_cutoff_hour >= 12;
                                  let newHour = hour;
                                  if (isPM && hour !== 12) newHour = hour + 12;
                                  if (!isPM && hour === 12) newHour = 0;
                                  setBookingRules(prev => ({ ...prev, same_day_cutoff_hour: newHour }));
                                }}
                              >
                                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (<SelectItem key={h} value={String(h)}>{h}</SelectItem>))}
                                </SelectContent>
                              </Select>
                              <span>:</span>
                              <Select
                                value={String(bookingRules.same_day_cutoff_minute)}
                                onValueChange={(val) => setBookingRules(prev => ({ ...prev, same_day_cutoff_minute: parseInt(val) }))}
                              >
                                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[0, 15, 30, 45].map(m => (<SelectItem key={m} value={String(m)}>{m.toString().padStart(2, '0')}</SelectItem>))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={bookingRules.same_day_cutoff_hour >= 12 ? 'PM' : 'AM'}
                                onValueChange={(val) => {
                                  const currentHour = bookingRules.same_day_cutoff_hour;
                                  const isPM = currentHour >= 12;
                                  if (val === 'PM' && !isPM) setBookingRules(prev => ({ ...prev, same_day_cutoff_hour: currentHour + 12 }));
                                  else if (val === 'AM' && isPM) setBookingRules(prev => ({ ...prev, same_day_cutoff_hour: currentHour - 12 }));
                                }}
                              >
                                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="AM">AM</SelectItem>
                                  <SelectItem value="PM">PM</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <p className="text-[11px] text-emerald-600 font-medium">Current cutoff: {formatCutoffTime()} ET</p>
                          </div>
                        )}
                      </div>
                      {/* Next-day (inline toggle) */}
                      <div className="p-3.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-gray-900">Allow Next-Day Bookings</p>
                            <p className="text-[11px] text-gray-500">Let customers book services for tomorrow</p>
                          </div>
                          <Switch
                            checked={bookingRules.allow_next_day}
                            onCheckedChange={(checked) => setBookingRules(prev => ({ ...prev, allow_next_day: checked }))}
                            data-testid="next-day-toggle"
                          />
                        </div>
                      </div>
                      {/* Minimum Notice (tap-to-edit) */}
                      <button
                        type="button"
                        onClick={() => setRulesEditor('min_notice')}
                        className="w-full p-3.5 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors"
                        data-testid="rules-row-min-notice"
                      >
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-gray-900">Minimum Notice</p>
                          <p className="text-[11px] text-gray-500">
                            {minNoticeDays > 0
                              ? `${minNoticeDays} day${minNoticeDays > 1 ? 's' : ''} advance notice required`
                              : 'No minimum notice'}
                          </p>
                        </div>
                        <span className="text-[11px] font-medium text-gray-400 shrink-0">Edit &rarr;</span>
                      </button>
                    </div>
                  </div>

                  {/* ── 2. PAYMENT RULES ─────────────────────────── */}
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Payments</p>
                    <div className="bg-white rounded-xl border border-gray-100">
                      <button
                        type="button"
                        onClick={() => setRulesEditor('deposit')}
                        className="w-full p-3.5 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors rounded-xl"
                        data-testid="rules-row-deposit"
                      >
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-gray-900">Deposit Payment Window</p>
                          <p className="text-[11px] text-gray-500">
                            {bookingRules.deposit_window_hours || 48} hours
                            {bookingRules.allow_deposit_extensions ? ' · extensions allowed' : ''}
                          </p>
                        </div>
                        <span className="text-[11px] font-medium text-gray-400 shrink-0">Edit &rarr;</span>
                      </button>
                    </div>
                  </div>

                  {/* ── 3. PRICING INCENTIVES ────────────────────── */}
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Pricing Incentives</p>
                    <div className="bg-white rounded-xl border border-gray-100">
                      <button
                        type="button"
                        onClick={() => setRulesEditor('discount')}
                        className="w-full p-3.5 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-colors rounded-xl"
                        data-testid="rules-row-discount"
                      >
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-gray-900">Pay-in-Full Discount</p>
                          <p className="text-[11px] text-gray-500">
                            {bookingRules.full_payment_discount_enabled && bookingRules.full_payment_discount_percent > 0
                              ? `${bookingRules.full_payment_discount_percent}% off for upfront payment`
                              : 'Not offered'}
                          </p>
                        </div>
                        <span className="text-[11px] font-medium text-gray-400 shrink-0">Edit &rarr;</span>
                      </button>
                    </div>
                  </div>

                  {/* ── SUMMARY (read-only, kept) ────────────── */}
                  <div className="bg-emerald-50/60 p-3.5 rounded-xl border border-emerald-100/60">
                    <h4 className="text-[12px] font-semibold text-emerald-800 mb-1.5 uppercase tracking-wider">Your Availability</h4>
                    <ul className="text-[12px] text-emerald-700/90 space-y-1">
                      {bookingRules.allow_same_day && (
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 shrink-0" />Same-day bookings accepted (before {formatCutoffTime()} ET)</li>
                      )}
                      {bookingRules.allow_next_day && (
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 shrink-0" />Next-day bookings accepted</li>
                      )}
                      {minNoticeDays > 0 && (
                        <li className="flex items-center gap-2"><Clock className="h-3 w-3 shrink-0" />{minNoticeDays} day{minNoticeDays > 1 ? 's' : ''} notice for other bookings</li>
                      )}
                      {!bookingRules.allow_same_day && !bookingRules.allow_next_day && minNoticeDays === 0 && (
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 shrink-0" />All available dates open for booking</li>
                      )}
                      {bookingRules.full_payment_discount_enabled && bookingRules.full_payment_discount_percent > 0 && (
                        <li className="flex items-center gap-2"><Check className="h-3 w-3 shrink-0" />{bookingRules.full_payment_discount_percent}% discount for full upfront payment</li>
                      )}
                      <li className="flex items-center gap-2"><Clock className="h-3 w-3 shrink-0" />{bookingRules.deposit_window_hours || 48} hours for deposit{bookingRules.allow_deposit_extensions ? ' (extendable)' : ''}</li>
                    </ul>
                  </div>

                  <Button
                    onClick={saveBookingRules}
                    disabled={savingBookingRules}
                    className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 h-10 text-[13px]"
                    data-testid="save-booking-rules-btn"
                  >
                    {savingBookingRules ? 'Saving...' : 'Save Booking Rules'}
                  </Button>
                </div>
              </div>
            )}

            {/* ── RULES FOCUSED EDITOR DRAWER (tap-to-edit) ────────── */}
            {rulesEditor && (
              <div
                data-testid="rules-editor-overlay"
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
                onClick={() => setRulesEditor(null)}
                style={rulesKeyboardInset > 0 ? { paddingBottom: `${rulesKeyboardInset}px` } : undefined}
              >
                <div
                  className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden pb-[calc(env(safe-area-inset-bottom)+80px)] md:pb-0"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="rules-editor-panel"
                >
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <button type="button" onClick={() => setRulesEditor(null)} className="p-1 text-gray-400 hover:text-gray-600" data-testid="rules-editor-close-btn">
                      <X className="h-4 w-4" />
                    </button>
                    <h3 className="text-[14px] font-semibold text-gray-900">
                      {rulesEditor === 'min_notice' && 'Minimum Notice'}
                      {rulesEditor === 'deposit' && 'Deposit Payment Window'}
                      {rulesEditor === 'discount' && 'Pay-in-Full Discount'}
                    </h3>
                    <div className="w-6" />
                  </div>
                  <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">

                    {rulesEditor === 'min_notice' && (
                      <>
                        <p className="text-[12px] text-gray-500">For bookings outside same-day/next-day, how much advance notice do you need?</p>
                        <div className="flex items-center gap-2">
                          <Input
                            type="text" inputMode="numeric" pattern="[0-9]*"
                            value={String(minNoticeDays)}
                            onChange={(e) => {
                              // Strip any non-digits and leading zeros so the
                              // field never displays values like "010".
                              const digits = e.target.value.replace(/\D/g, '');
                              const cleaned = digits.replace(/^0+(?=\d)/, '') || '0';
                              const n = Math.min(365, parseInt(cleaned, 10) || 0);
                              // Force the DOM to match normalized value even when
                              // the parsed state would otherwise be unchanged.
                              if (e.target.value !== String(n)) {
                                e.target.value = String(n);
                              }
                              handleMinNoticeDaysChange(n);
                            }}
                            className="w-24"
                            data-testid="min-notice-input"
                          />
                          <span className="text-[13px] text-gray-600">days</span>
                        </div>
                        {minNoticeDays > 0 && (
                          <p className="text-[11px] text-emerald-600">Customers must book at least {minNoticeDays} day{minNoticeDays > 1 ? 's' : ''} in advance.</p>
                        )}
                      </>
                    )}

                    {rulesEditor === 'deposit' && (
                      <>
                        <p className="text-[12px] text-gray-500">How long customers have to pay their deposit after accepting a proposal.</p>
                        <div className="space-y-1">
                          <Label className="text-[12px]">Time allowed</Label>
                          <Select
                            value={bookingRules.deposit_window_hours?.toString() || '48'}
                            onValueChange={(value) => setBookingRules(prev => ({ ...prev, deposit_window_hours: parseInt(value) }))}
                          >
                            <SelectTrigger className="w-full" data-testid="deposit-window-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="24">24 hours (1 day)</SelectItem>
                              <SelectItem value="48">48 hours (2 days)</SelectItem>
                              <SelectItem value="72">72 hours (3 days)</SelectItem>
                              <SelectItem value="96">96 hours (4 days)</SelectItem>
                              <SelectItem value="120">120 hours (5 days)</SelectItem>
                              <SelectItem value="144">144 hours (6 days)</SelectItem>
                              <SelectItem value="168">168 hours (7 days)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between gap-3 pt-2">
                          <div className="min-w-0">
                            <Label className="text-[13px] font-medium">Allow per-booking extensions</Label>
                            <p className="text-[11px] text-gray-500">Extend the window for specific bookings (max 3 times)</p>
                          </div>
                          <Switch
                            checked={bookingRules.allow_deposit_extensions}
                            onCheckedChange={(checked) => setBookingRules(prev => ({ ...prev, allow_deposit_extensions: checked }))}
                            data-testid="allow-extensions-toggle"
                          />
                        </div>
                      </>
                    )}

                    {rulesEditor === 'discount' && (
                      <>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <Label className="text-[13px] font-medium">Offer discount</Label>
                            <p className="text-[11px] text-gray-500">Reward customers who pay the full amount upfront.</p>
                          </div>
                          <Switch
                            checked={bookingRules.full_payment_discount_enabled}
                            onCheckedChange={(checked) => setBookingRules(prev => ({
                              ...prev,
                              full_payment_discount_enabled: checked,
                              full_payment_discount_percent: checked && prev.full_payment_discount_percent === 0 ? 3 : prev.full_payment_discount_percent,
                            }))}
                            data-testid="full-payment-discount-toggle"
                          />
                        </div>
                        {bookingRules.full_payment_discount_enabled && (
                          <div className="space-y-1 pt-2">
                            <Label className="text-[12px]">Discount percentage (max 10%)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*"
                                value={bookingRules.full_payment_discount_percent}
                                onChange={(e) => {
                                  const input = e.target.value;
                                  if (input === '' || /^\d*\.?\d*$/.test(input)) {
                                    const val = input === '' ? 0 : parseFloat(input);
                                    if (!isNaN(val) && val <= 10) {
                                      setBookingRules(prev => ({ ...prev, full_payment_discount_percent: val }));
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  setBookingRules(prev => ({ ...prev, full_payment_discount_percent: Math.min(10, Math.max(0, val)) }));
                                }}
                                className="w-24"
                                data-testid="full-payment-discount-input"
                              />
                              <span className="text-[13px] text-gray-600">%</span>
                            </div>
                            {bookingRules.full_payment_discount_percent > 0 && (
                              <p className="text-[11px] text-emerald-600 font-medium">Customers see &quot;Pay in Full — Save {bookingRules.full_payment_discount_percent}%&quot; at checkout.</p>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    <div className="pt-2">
                      <Button
                        onClick={() => setRulesEditor(null)}
                        className="w-full rounded-xl bg-gray-900 hover:bg-gray-800 h-10 text-[13px]"
                        data-testid="rules-editor-done-btn"
                      >
                        Done
                      </Button>
                      <p className="text-[11px] text-gray-400 text-center mt-2">Tap &quot;Save Booking Rules&quot; on the main screen to persist changes.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── DAY DETAIL OVERLAY (Calendar tap) ────────────── */}
            {scheduleDayDetail && (() => {
              const selectedDateStr = scheduleDayDetail;
              const selectedDate = new Date(selectedDateStr);
              const isoDate = selectedDate.toISOString().slice(0, 10);
              const dayBookings = inquiries
                .map(inq => ({ inq, bk: getBookingForInquiry(inq.id) }))
                .filter(({ inq, bk }) => {
                  if (!bk) return false;
                  if (['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(bk.status)) return false;
                  const ed = bk.event_date || inq.event_date;
                  if (!ed) return false;
                  return new Date(ed).toISOString().slice(0, 10) === isoDate;
                });
              const isBlocked = (blockedDates || []).some(d => new Date(d).toISOString().slice(0, 10) === isoDate);
              const dateLabel = selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              return (
                <div
                  data-testid="schedule-day-detail-overlay"
                  className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
                  onClick={() => setScheduleDayDetail(null)}
                >
                  <div
                    className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden pb-[calc(env(safe-area-inset-bottom)+80px)] md:pb-0"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="schedule-day-detail-panel"
                  >
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <button type="button" onClick={() => setScheduleDayDetail(null)} className="p-1 text-gray-400 hover:text-gray-600" data-testid="schedule-day-detail-close-btn">
                        <X className="h-4 w-4" />
                      </button>
                      <h3 className="text-[14px] font-semibold text-gray-900">{dateLabel}</h3>
                      <div className="w-6" />
                    </div>
                    <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                      {dayBookings.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4 text-center">
                          {isBlocked ? 'This date is blocked.' : 'No bookings for this date.'}
                        </p>
                      ) : (
                        <>
                          <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">
                            {dayBookings.length} {dayBookings.length === 1 ? 'Booking' : 'Bookings'}
                          </p>
                          {dayBookings.map(({ inq, bk }) => (
                            <div key={bk.id || inq.id} className="p-3 rounded-xl border border-gray-100 bg-gray-50/60">
                              <p className="text-[13px] font-semibold text-gray-900">{inq.event_type || inq.selected_package || 'Booking'}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5">{inq.customer_name || 'Customer'}</p>
                              {bk.event_location && <p className="text-[11px] text-gray-400 mt-0.5">{bk.event_location}</p>}
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Day-detail row → Requests handoff (same rules
                                    // as Upcoming list above). Chat reachable from
                                    // the matching card's Message Customer button.
                                    setScheduleDayDetail(null);
                                    setRequestsFilter(getFilterForInquiry(inq));
                                    setHighlightInquiryId(inq.id);
                                    setActiveTab('inquiries');
                                  }}
                                  className="text-[11px] font-medium text-emerald-700 hover:text-emerald-800"
                                  data-testid={`day-detail-view-requests-${bk.id || inq.id}`}
                                >
                                  View Request &rarr;
                                </button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                      <div className="pt-2 border-t border-gray-100 mt-2">
                        {isBlocked ? (
                          <button type="button" onClick={() => { removeBlockedDate(selectedDate); setScheduleDayDetail(null); }} className="w-full text-[12px] font-medium text-emerald-700 hover:text-emerald-800 py-2" data-testid="day-detail-unblock-btn">
                            Unblock this date
                          </button>
                        ) : dayBookings.length === 0 ? (
                          <button type="button" onClick={() => { handleDateSelect(selectedDate); setScheduleDayDetail(null); }} className="w-full text-[12px] font-medium text-red-600 hover:text-red-700 py-2" data-testid="day-detail-block-btn">
                            Block this date
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="inquiries">
            {/* How Jobs Work Mini-Guide */}
            <div className="mb-4 bg-emerald-50/50 rounded-xl border border-emerald-100 overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-2.5 text-left"
                onClick={() => {
                  const newState = !showJobsGuide;
                  setShowJobsGuide(newState);
                  localStorage.setItem('vendorJobsGuideExpanded', newState.toString());
                }}
              >
                <span className="text-[13px] font-semibold text-emerald-800 flex items-center gap-2">
                  <ListOrdered className="w-4 h-4" />
                  How Jobs Work
                </span>
                {showJobsGuide ? (
                  <ChevronUp className="w-4 h-4 text-emerald-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-emerald-500" />
                )}
              </button>
              {showJobsGuide && (
                <div className="px-4 pb-3">
                  <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-[12px] text-emerald-700">
                    {['Receive a request', 'Message to confirm details', 'Send a proposal', 'Customer pays', 'Complete the service'].map((step, i) => (
                      <span key={i} className="flex items-center gap-1.5">
                        <span className="h-4 w-4 rounded-full bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        {step}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div data-testid="inquiries-card">
              {/* One-time transition notice: Chats → Requests */}
              {showChatsMergedNotice && inquiries.length > 0 && (
                <div className="mb-3 flex items-start gap-2.5 bg-blue-50/70 border border-blue-100 rounded-xl px-3 py-2.5" data-testid="chats-merged-notice">
                  <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="flex-1 text-[12px] text-blue-800 leading-snug">
                    Chats now lives inside Requests. Tap <span className="font-semibold">Unread</span> to find active conversations quickly.
                  </p>
                  <button
                    onClick={dismissChatsMergedNotice}
                    className="text-blue-500 hover:text-blue-700 shrink-0"
                    data-testid="dismiss-chats-merged-notice"
                    aria-label="Dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Header: Title + Sort toggle + View mode toggle */}
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                  <h3 className="text-[15px] font-bold text-gray-900">Requests</h3>
                </div>
                {inquiries.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Sort toggle */}
                    <button
                      onClick={toggleInquirySortMode}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                        inquirySortMode === 'recent'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-white text-gray-500 border-gray-200 hover:text-gray-700'
                      }`}
                      data-testid="sort-mode-toggle"
                      title={inquirySortMode === 'recent' ? 'Sorted by recent activity — click to reset' : 'Sort by recent activity'}
                    >
                      <Clock className="h-3 w-3" />
                      Recent
                    </button>

                    {/* View mode */}
                    <div className="flex items-center gap-0.5 bg-gray-50 rounded-full p-0.5">
                      <button
                        onClick={() => setInquiryViewModeAndPersist('compact')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          inquiryViewMode === 'compact' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                        data-testid="view-mode-compact"
                      >
                        <MessageSquare className="h-3 w-3" />
                        Compact
                      </button>
                      <button
                        onClick={() => setInquiryViewModeAndPersist('detailed')}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          inquiryViewMode === 'detailed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}
                        data-testid="view-mode-detailed"
                      >
                        <ListOrdered className="h-3 w-3" />
                        Standard
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Lifecycle Filter Chips */}
              {inquiries.length > 0 && (
                <div ref={filterScrollRef} className="mb-4 -mx-1 px-1 overflow-x-auto scrollbar-hide" data-testid="request-filter-tabs">
                  <div className="flex gap-1.5 min-w-max pb-1">
                      {(() => {
                        // Pre-compute lifecycle state once per inquiry so every
                        // chip count, the filter map, and the Payments overlay
                        // share the same truth. Prevents drift between count
                        // and rendered list.
                        const stateByInq = new Map(inquiries.map(i => [i.id, getInquiryLifecycleState(i)]));
                        const stateOf = (i) => stateByInq.get(i.id);
                        // Belt-and-suspenders: an inquiry/booking whose event
                        // date has already passed is never an active payment
                        // chase. The classifier already routes the standard
                        // case (booking + event past → ready_to_close), but
                        // this guard catches edge data where inquiry.event_date
                        // is missing yet booking.event_date is set.
                        const eventInPast = (i) => {
                          const bk = getBookingForInquiry(i.id);
                          const ed = bk?.event_date || i.event_date;
                          if (!ed) return false;
                          const d = new Date(ed);
                          d.setHours(23, 59, 59, 999);
                          return d < new Date();
                        };
                        const inPaymentsChip = (i) => {
                          const s = stateOf(i);
                          if (['canceled', 'completed', 'ready_to_close'].includes(s)) return false;
                          if (eventInPast(i)) return false;
                          return getPaymentStatus(i, getBookingForInquiry(i.id)).show_in_payments_tab;
                        };
                        const tabs = [
                          { key: 'all', label: 'All', count: inquiries.length },
                          // Unread excludes terminal states (canceled/completed)
                          // so leftover unread on a closed thread cannot
                          // pollute the chip with non-actionable rows.
                          { key: 'unread', label: 'Unread', count: inquiries.filter(i => (i.unread_count || 0) > 0 && !['canceled', 'completed'].includes(stateOf(i))).length },
                          { key: 'needs_response', label: 'Needs Response', count: inquiries.filter(i => stateOf(i) === 'needs_response').length },
                          { key: 'in_progress', label: 'In Progress', count: inquiries.filter(i => stateOf(i) === 'in_progress').length },
                          { key: 'pending_payment', label: 'Pending Payment', count: inquiries.filter(i => stateOf(i) === 'pending_payment').length },
                          { key: 'booked', label: 'Booked', count: inquiries.filter(i => stateOf(i) === 'booked').length },
                          { key: 'payments', label: 'Payments', count: inquiries.filter(inPaymentsChip).length },
                          { key: 'ready_to_close', label: 'Ready to Close', count: inquiries.filter(i => stateOf(i) === 'ready_to_close').length },
                          { key: 'completed', label: 'Completed', count: inquiries.filter(i => stateOf(i) === 'completed').length },
                          { key: 'canceled', label: 'Canceled', count: inquiries.filter(i => stateOf(i) === 'canceled').length },
                        ];
                        return tabs.map(tab => (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setRequestsFilter(tab.key)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                              requestsFilter === tab.key
                                ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            data-testid={`filter-${tab.key}`}
                          >
                            {tab.label}{tab.count > 0 ? ` (${tab.count})` : ''}
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}

              {/* Content */}
              {loading ? (
                  <InquiryListSkeleton count={3} />
                ) : inquiries.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-inquiries">
                    <div className="max-w-md mx-auto">
                      <Inbox className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      {!vendorProfile ? (
                        <>
                          <h3 className="text-[15px] font-semibold text-gray-900 mb-1">Complete Your Profile First</h3>
                          <p className="text-[13px] text-gray-500 mb-4">You need to complete your vendor profile before you can receive requests.</p>
                          <Button 
                            onClick={() => setActiveTab('profile')}
                            className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-9 px-5 text-[13px]"
                          >
                            Complete Profile Now
                          </Button>
                        </>
                      ) : (
                        <>
                          <h3 className="text-[15px] font-semibold text-gray-900 mb-1">No Requests Yet</h3>
                          <p className="text-[13px] text-gray-500">Once customers find your profile and send requests, they'll appear here.</p>
                        </>
                      )}
                    </div>
                  </div>
                ) : (() => {
                  // Apply lifecycle filter using the centralised classifier
                  // so the filtered list cannot drift from the chip counts.
                  const filterMap = {
                    all: () => true,
                    // Unread overlay — excludes terminal states.
                    unread: (i) => (i.unread_count || 0) > 0 && !['canceled', 'completed'].includes(getInquiryLifecycleState(i)),
                    needs_response: (i) => getInquiryLifecycleState(i) === 'needs_response',
                    in_progress: (i) => getInquiryLifecycleState(i) === 'in_progress',
                    pending_payment: (i) => getInquiryLifecycleState(i) === 'pending_payment',
                    booked: (i) => getInquiryLifecycleState(i) === 'booked',
                    // Payments overlay — explicitly excludes canceled,
                    // completed, ready_to_close, and any inquiry whose event
                    // date has already passed so terminal/past-event rows
                    // can never leak into the Payments chip.
                    payments: (i) => {
                      const s = getInquiryLifecycleState(i);
                      if (['canceled', 'completed', 'ready_to_close'].includes(s)) return false;
                      const bk = getBookingForInquiry(i.id);
                      const ed = bk?.event_date || i.event_date;
                      if (ed) {
                        const d = new Date(ed);
                        d.setHours(23, 59, 59, 999);
                        if (d < new Date()) return false;
                      }
                      return getPaymentStatus(i, bk).show_in_payments_tab;
                    },
                    ready_to_close: (i) => getInquiryLifecycleState(i) === 'ready_to_close',
                    completed: (i) => getInquiryLifecycleState(i) === 'completed',
                    canceled: (i) => getInquiryLifecycleState(i) === 'canceled',
                  };
                  const filterFn = filterMap[requestsFilter] || filterMap.all;
                  // Recent Activity sort: use last_message_at if present, else updated_at, else created_at.
                  // Always non-destructive — sorts a copy.
                  const activityTs = (i) => {
                    const t = i.last_message_at || i.updated_at || i.created_at;
                    return t ? new Date(t).getTime() : 0;
                  };
                  const base = inquiries.filter(filterFn);
                  const filtered = inquirySortMode === 'recent'
                    ? [...base].sort((a, b) => activityTs(b) - activityTs(a))
                    : base;

                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-400">No requests match this filter</p>
                        <button type="button" onClick={() => setRequestsFilter('all')} className="text-sm text-emerald-600 hover:text-emerald-700 mt-2 font-medium">
                          View all requests
                        </button>
                      </div>
                    );
                  }

                  // Special grouped view for Payments filter
                  if (requestsFilter === 'payments') {
                    const urgent = filtered.filter(i => {
                      const ps = getPaymentStatus(i, getBookingForInquiry(i.id));
                      return ps.urgency === 'high';
                    });
                    const attention = filtered.filter(i => {
                      const ps = getPaymentStatus(i, getBookingForInquiry(i.id));
                      return ps.urgency === 'medium';
                    });

                    return (
                      <>
                        {urgent.length > 0 && (
                          <div className="mb-5">
                            <div className="flex items-center gap-2 px-1 mb-2.5">
                              <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Urgent ({urgent.length})</span>
                            </div>
                            <div className="space-y-3">
                              {urgent.map((inquiry) => (
                                <VendorInquiryCard key={inquiry.id} inquiry={inquiry} bookings={bookings} getStatusBadge={getStatusBadge} getBookingForInquiry={getBookingForInquiry} handleOpenMessaging={handleOpenMessaging} setProgressNoteInquiry={setProgressNoteInquiry} setOnHoldInquiry={setOnHoldInquiry} setDecliningInquiry={setDecliningInquiry} markCompleted={markCompleted} openStartWorkDialog={openStartWorkDialog} setShowServiceAgreement={setShowServiceAgreement} setSelectedBooking={setSelectedBooking} onBookingCancelled={handleBookingCancelled} onBookingUpdated={handleBookingUpdated} viewMode={inquiryViewMode} highlight={inquiry.id === highlightInquiryId} />
                              ))}
                            </div>
                          </div>
                        )}
                        {attention.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 px-1 mb-2.5">
                              <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Needs Attention ({attention.length})</span>
                            </div>
                            <div className="space-y-3">
                              {attention.map((inquiry) => (
                                <VendorInquiryCard key={inquiry.id} inquiry={inquiry} bookings={bookings} getStatusBadge={getStatusBadge} getBookingForInquiry={getBookingForInquiry} handleOpenMessaging={handleOpenMessaging} setProgressNoteInquiry={setProgressNoteInquiry} setOnHoldInquiry={setOnHoldInquiry} setDecliningInquiry={setDecliningInquiry} markCompleted={markCompleted} openStartWorkDialog={openStartWorkDialog} setShowServiceAgreement={setShowServiceAgreement} setSelectedBooking={setSelectedBooking} onBookingCancelled={handleBookingCancelled} onBookingUpdated={handleBookingUpdated} viewMode={inquiryViewMode} highlight={inquiry.id === highlightInquiryId} />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }

                  // Split into active and closed for display
                  const activeItems = filtered.filter(inq => !['declined', 'cancelled', 'completed'].includes(inq.status));
                  const closedItems = filtered.filter(inq => ['declined', 'cancelled', 'completed'].includes(inq.status));

                  return (
                    <>
                      {activeItems.length > 0 && (
                        <div className={closedItems.length > 0 ? 'mb-6' : ''}>
                          {requestsFilter === 'all' && (
                            <div className="flex items-center gap-2 px-1 mb-2.5">
                              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Active</span>
                            </div>
                          )}
                          <div className="space-y-3">
                            {activeItems.map((inquiry) => (
                              <VendorInquiryCard
                                key={inquiry.id}
                                inquiry={inquiry}
                                bookings={bookings}
                                getStatusBadge={getStatusBadge}
                                getBookingForInquiry={getBookingForInquiry}
                                handleOpenMessaging={handleOpenMessaging}
                                setProgressNoteInquiry={setProgressNoteInquiry}
                                setOnHoldInquiry={setOnHoldInquiry}
                                setDecliningInquiry={setDecliningInquiry}
                                markCompleted={markCompleted}
                                openStartWorkDialog={openStartWorkDialog}
                                setShowServiceAgreement={setShowServiceAgreement}
                                setSelectedBooking={setSelectedBooking}
                                onBookingCancelled={handleBookingCancelled}
                                onBookingUpdated={handleBookingUpdated}
                                viewMode={inquiryViewMode}
                                highlight={inquiry.id === highlightInquiryId}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {closedItems.length > 0 && (
                        <div className={activeItems.length > 0 ? 'pt-5' : ''}>
                          {requestsFilter === 'all' && (
                            <div className="flex items-center gap-2 px-1 mb-2.5">
                              <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Closed</span>
                            </div>
                          )}
                          <div className={`space-y-3 ${requestsFilter === 'all' ? 'opacity-70' : ''}`}>
                            {closedItems.map((inquiry) => (
                              <VendorInquiryCard
                                key={inquiry.id}
                                inquiry={inquiry}
                                bookings={bookings}
                                getStatusBadge={getStatusBadge}
                                getBookingForInquiry={getBookingForInquiry}
                                handleOpenMessaging={handleOpenMessaging}
                                setProgressNoteInquiry={setProgressNoteInquiry}
                                setOnHoldInquiry={setOnHoldInquiry}
                                setDecliningInquiry={setDecliningInquiry}
                                markCompleted={markCompleted}
                                openStartWorkDialog={openStartWorkDialog}
                                setShowServiceAgreement={setShowServiceAgreement}
                                setSelectedBooking={setSelectedBooking}
                                onBookingCancelled={handleBookingCancelled}
                                onBookingUpdated={handleBookingUpdated}
                                viewMode={inquiryViewMode}
                                highlight={inquiry.id === highlightInquiryId}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          </TabsContent>

          {/* Account Settings Tab */}
          <TabsContent value="account">
            <AccountPage
              variant="vendor"
              onNavigate={(tab) => setActiveTab(tab)}
              onLogout={() => {
                // Feb 2026: logout() owns the route transition. A caller-
                // side navigate raced ahead of setUser(null) and caused
                // AuthPage to redirect a still-truthy user back here.
                logout();
              }}
              onFeedbackClick={() => setFeedbackOpen(true)}
              profileVisibility={profileVisibility}
              toggleProfileVisibility={toggleProfileVisibility}
              updatingVisibility={updatingVisibility}
              vendorProfile={vendorProfile}
            />
          </TabsContent>

          {/* Account Referrals Tab */}
          <TabsContent value="account-referrals">
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setActiveTab('account')}
                className="text-gray-600"
                data-testid="back-to-account-from-referrals"
              >
                &larr; Back to Account
              </Button>
            </div>
            <ReferralRewardsCard user={user} profileStrength={onboardingStatus?.profile_strength} />
          </TabsContent>

          {/* Account Security Tab - Full form view */}
          <TabsContent value="account-security">
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setActiveTab('account')}
                className="text-gray-600"
              >
                ← Back to Account
              </Button>
            </div>
            <VendorAccountSettings
              user={user}
              newEmail={newEmail}
              setNewEmail={setNewEmail}
              newPhone={newPhone}
              setNewPhone={setNewPhone}
              passwordData={passwordData}
              setPasswordData={setPasswordData}
              securityQuestions={securityQuestions}
              securityAnswers={securityAnswers}
              setSecurityAnswers={setSecurityAnswers}
              currentPasswordForSecurity={currentPasswordForSecurity}
              setCurrentPasswordForSecurity={setCurrentPasswordForSecurity}
              updatingEmail={updatingEmail}
              updatingPhone={updatingPhone}
              updatingPassword={updatingPassword}
              updatingSecurityQuestions={updatingSecurityQuestions}
              updateEmail={updateEmail}
              updatePhone={updatePhone}
              handlePasswordChange={handlePasswordChange}
              handleSecurityQuestionsUpdate={handleSecurityQuestionsUpdate}
            />
          </TabsContent>

          {/* Visibility Settings Tab */}
          <TabsContent value="visibility-settings">
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setActiveTab('account')}
                className="text-gray-600"
              >
                ← Back to Account
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {profileVisibility?.is_visible_to_customers ? (
                    <Eye className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-gray-500" />
                  )}
                  Profile & Visibility
                </CardTitle>
                <CardDescription>
                  Control whether customers can find and contact you
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Admin Block Warning */}
                {profileVisibility?.admin_blocked && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <ShieldX className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Profile Hidden by Admin</p>
                      <p className="text-sm text-red-700 mt-1">
                        Your profile has been hidden by a Celey administrator. Please contact support for more information.
                      </p>
                      <a 
                        href="mailto:celeyapp@gmail.com" 
                        className="text-sm text-red-600 hover:underline mt-2 inline-block"
                      >
                        Contact Support →
                      </a>
                    </div>
                  </div>
                )}

                {/* Profile Active Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">Profile Active</p>
                      <Badge 
                        variant={profileVisibility?.vendor_active ? 'success' : 'secondary'}
                        className={profileVisibility?.vendor_active ? 'bg-emerald-100 text-emerald-800' : ''}
                      >
                        {profileVisibility?.vendor_active ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      When active, your profile can appear in browse results and customers can send you requests.
                    </p>
                  </div>
                  <Switch
                    checked={profileVisibility?.vendor_active || false}
                    onCheckedChange={(checked) => toggleProfileVisibility(checked)}
                    disabled={updatingVisibility || profileVisibility?.admin_blocked}
                  />
                </div>

                {/* Visibility Status Summary */}
                <div className="p-4 border rounded-lg">
                  <p className="font-medium text-gray-900 mb-3">Current Visibility Status</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Profile Active (Your Choice)</span>
                      <span className={profileVisibility?.vendor_active ? 'text-emerald-600 font-medium' : 'text-gray-500'}>
                        {profileVisibility?.vendor_active ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Admin Approved</span>
                      <span className={profileVisibility?.admin_visible ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                        {profileVisibility?.admin_visible ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Profile Complete</span>
                      <span className={profileVisibility?.profile_complete ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}>
                        {profileVisibility?.profile_complete ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="font-medium text-gray-900">Visible to Customers</span>
                      <span className={profileVisibility?.is_visible_to_customers ? 'text-emerald-600 font-medium' : 'text-gray-500'}>
                        {profileVisibility?.is_visible_to_customers ? 'Yes ✓' : 'No'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Incomplete profile notice - only shown when fields are missing */}
                  {profileVisibility?.missing_fields?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed text-sm text-amber-700">
                      <p className="mb-1">To appear in browse results, add:</p>
                      <ul className="text-amber-600 space-y-0.5">
                        {profileVisibility.missing_fields.includes('business_name') && (
                          <li>• Business name</li>
                        )}
                        {profileVisibility.missing_fields.includes('location') && (
                          <li>• City or state</li>
                        )}
                        {profileVisibility.missing_fields.includes('profile_photo') && (
                          <li>• Profile photo</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Help Text Card */}
            <Card className="mt-4">
              <CardContent className="pt-4">
                <div className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
                  <p className="font-medium text-blue-800 mb-1">💡 Tip</p>
                  <p className="text-blue-700">
                    Toggle your profile off temporarily if you&apos;re unavailable for new bookings. 
                    Your existing conversations and bookings won&apos;t be affected.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Help & Support Tab */}
          <TabsContent value="help">
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setActiveTab('account')}
                className="text-gray-600"
              >
                ← Back to Account
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600" />
                  Help & Support
                </CardTitle>
                <CardDescription>
                  Get answers to common questions and contact support
                </CardDescription>
              </CardHeader>
              <HelpSupportSection 
                userRole="vendor"
                onFeedbackClick={() => setFeedbackOpen(true)}
                embedded={true}
              />
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentsTab 
              onboardingStatus={onboardingStatus}
              stripeConnecting={stripeConnecting}
              handleStripeConnect={handleStripeConnect}
              API={API}
            />
          </TabsContent>

          {/* Tips Tab */}
          <TabsContent value="tips">
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                  <Lightbulb className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Success Tips for Service Providers</h2>
                <p className="text-gray-600 mt-2">Follow these best practices to grow your business and get more bookings</p>
              </div>

              {/* Profile Setup — aligned with Profile Strength scoring */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" id="tips-setup">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg text-blue-800">Profile Setup</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-blue-900 space-y-3">
                  <p className="font-medium">Complete these steps to maximize your visibility and start receiving bookings:</p>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {onboardingStatus?.profile_strength?.breakdown ? (
                      Object.entries(onboardingStatus.profile_strength.breakdown).map(([key, item]) => (
                        <div key={key} className="flex items-start gap-2">
                          {item.complete ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Circle className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm ${item.complete ? 'text-blue-600 line-through' : 'text-blue-900 font-medium'}`}>{item.label}</span>
                        </div>
                      ))
                    ) : (
                      <>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Business name & location</span></div>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Profile photo</span></div>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Service description</span></div>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Pricing information</span></div>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Portfolio images (3+)</span></div>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Stripe payouts connected</span></div>
                        <div className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Availability / booking rules</span></div>
                      </>
                    )}
                  </div>
                  <div className="pt-2 border-t border-blue-200 mt-3">
                    <p className="text-xs text-blue-600">
                      <strong>Tip:</strong> When setting up Stripe, select &quot;Individual/Sole proprietor&quot; as your business type and use your Celey profile URL or social media link as your website.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Vendor Guide CTA */}
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-amber-800">New to Event Services?</p>
                        <p className="text-sm text-amber-700">Check out our Service Provider Guide for checklists and tips by service type</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                      onClick={() => navigate('/vendor-guide')}
                    >
                      View Service Provider Guide
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Tips Grid */}
              <div className="grid gap-6 md:grid-cols-2">
                
                {/* Tip 1: Complete Your Profile */}
                <Card id="tips-profile" className="border-l-4 border-l-emerald-500 scroll-mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <CardTitle className="text-lg">Complete Your Profile 100%</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <p>A complete profile is <strong>3x more likely</strong> to receive requests!</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Add a professional profile photo</li>
                      <li>Upload 3-6 high-quality portfolio images</li>
                      <li>Write a detailed bio about your services</li>
                      <li>List all your service categories</li>
                      <li>Include your service area and pricing</li>
                    </ul>
                    <div className="pt-3">
                      <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => setActiveTab('profile')} data-testid="tips-edit-profile-btn">
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Edit Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Tip 2: Add Great Photos */}
                <Card id="tips-photos" className="border-l-4 border-l-blue-500 scroll-mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Camera className="w-5 h-5 text-blue-600" />
                      </div>
                      <CardTitle className="text-lg">Showcase Your Best Work</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <p>Great photos are your #1 marketing tool!</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Use bright, well-lit photos</li>
                      <li>Show variety in your work</li>
                      <li>Include close-ups and wide shots</li>
                      <li>Update photos regularly with new work</li>
                      <li>Ask happy clients if you can photograph their events</li>
                    </ul>
                    <div className="pt-3">
                      <Button variant="outline" size="sm" className="text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => setActiveTab('profile')} data-testid="tips-edit-photos-btn">
                        <Camera className="w-3.5 h-3.5 mr-1.5" />Edit Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Tip 3: Respond Quickly */}
                <Card id="tips-requests" className="border-l-4 border-l-amber-500 scroll-mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <CardTitle className="text-lg">Respond to Inquiries Quickly</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <p>Fast responses win more bookings!</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Check your dashboard daily</strong> for new requests</li>
                      <li>Aim to respond within 24 hours</li>
                      <li>Even a quick &quot;I&apos;ll get back to you&quot; shows you care</li>
                      <li>Set a reminder to check the app each morning</li>
                      <li>Customers often book the first vendor who responds</li>
                    </ul>
                    <div className="pt-3">
                      <Button variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => setActiveTab('inquiries')} data-testid="tips-view-requests-btn">
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" />View Requests
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Tip 4: Communication */}
                <Card id="tips-communication" className="border-l-4 border-l-purple-500 scroll-mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <MessageCircle className="w-5 h-5 text-purple-600" />
                      </div>
                      <CardTitle className="text-lg">Communicate Professionally</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <p>Great communication leads to great reviews!</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Be friendly, professional, and helpful</li>
                      <li>Ask clarifying questions about their event</li>
                      <li>Be clear about your pricing and what&apos;s included</li>
                      <li>Keep customers updated on their booking status</li>
                      <li>Send a thank you message after the event</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Tip 5: Get Reviews */}
                <Card id="tips-reviews" className="border-l-4 border-l-yellow-500 scroll-mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Star className="w-5 h-5 text-yellow-600" />
                      </div>
                      <CardTitle className="text-lg">Build Your Reviews</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <p>Reviews build trust and attract new customers!</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Deliver excellent service every time</li>
                      <li>Go above and beyond when you can</li>
                      <li>Politely ask satisfied customers to leave a review</li>
                      <li>Respond graciously to all reviews</li>
                      <li>Use feedback to improve your service</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Tip 6: Stay Active */}
                <Card id="tips-visibility" className="border-l-4 border-l-rose-500 scroll-mt-4">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-rose-100 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-rose-600" />
                      </div>
                      <CardTitle className="text-lg">Stay Active & Visible</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-600 space-y-2">
                    <p>Active vendors get more visibility!</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Log in regularly</strong> to check for messages</li>
                      <li>Keep your calendar up to date</li>
                      <li>Update your pricing as needed</li>
                      <li>Add new photos from recent events</li>
                      <li>Share your Celey profile on social media</li>
                    </ul>
                    <div className="pt-3">
                      <Button variant="outline" size="sm" className="text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => setActiveTab('calendar')} data-testid="tips-update-calendar-btn">
                        <Calendar className="w-3.5 h-3.5 mr-1.5" />Update Calendar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-lg text-emerald-800">Quick Actions to Get Started</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      variant="outline" 
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      onClick={() => setActiveTab('profile')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Update My Profile
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      onClick={() => setActiveTab('inquiries')}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Check Requests
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      onClick={() => setActiveTab('calendar')}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Update Calendar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Reminder Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-blue-800">Daily Reminder</p>
                  <p className="text-sm text-blue-600">
                    Make it a habit to check your Celey dashboard at least once a day. 
                    New requests come in all the time, and the fastest responders often win the booking!
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Feedback Dialog */}
      <FeedbackDialog 
        isOpen={feedbackOpen} 
        onClose={() => {
          setFeedbackOpen(false);
          setReportingInquiry(null);
        }} 
        userRole="vendor"
        inquiries={inquiries}
        preSelectedInquiry={reportingInquiry}
      />

      {/* Vendor Inquiry Dialogs */}
      <VendorInquiryDialogs
        cancellingInquiry={cancellingInquiry}
        setCancellingInquiry={setCancellingInquiry}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
        handleCancelInquiry={handleCancelInquiry}
        decliningInquiry={decliningInquiry}
        setDecliningInquiry={setDecliningInquiry}
        declineReason={declineReason}
        setDeclineReason={setDeclineReason}
        customDeclineReason={customDeclineReason}
        setCustomDeclineReason={setCustomDeclineReason}
        handleDeclineInquiry={handleDeclineInquiry}
        onHoldInquiry={onHoldInquiry}
        setOnHoldInquiry={setOnHoldInquiry}
        onHoldNote={onHoldNote}
        setOnHoldNote={setOnHoldNote}
        putOnHold={putOnHold}
        progressNoteInquiry={progressNoteInquiry}
        setProgressNoteInquiry={setProgressNoteInquiry}
        progressNote={progressNote}
        setProgressNote={setProgressNote}
        addProgressNote={addProgressNote}
        startingWorkInquiry={startingWorkInquiry}
        setStartingWorkInquiry={setStartingWorkInquiry}
        startWorkNote={startWorkNote}
        setStartWorkNote={setStartWorkNote}
        markInProgress={markInProgress}
        loading={loading}
        addingNote={addingNote}
        setAddingNote={setAddingNote}
        newProgressNote={newProgressNote}
        setNewProgressNote={setNewProgressNote}
        submitProgressNote={submitProgressNote}
        markingComplete={markingComplete}
        setMarkingComplete={setMarkingComplete}
        handleMarkComplete={handleMarkComplete}
      />


      {/* Service Agreement Dialog */}
      <ServiceAgreementDialog
        bookingId={selectedBooking?.id}
        isOpen={showServiceAgreement}
        onClose={() => setShowServiceAgreement(false)}
        onAccepted={handleServiceAgreementAccepted}
        userRole="vendor"
      />

      {/* Messaging Thread — Feature-flagged via ConversationSwitcher */}
      <ConversationSwitcher
        inquiryId={selectedInquiryForMessaging?.id}
        isOpen={showMessaging}
        onClose={handleCloseMessaging}
        userRole="vendor"
        onProposalAccepted={handleProposalAccepted}
        inquiryStatus={selectedInquiryForMessaging?.status}
        __diagSelectedInquiry={selectedInquiryForMessaging}
      />

      {/* Mobile Bottom Tab Bar - Vendor */}
      <BottomTabBar 
        activeTab={activeTab}
        onTabChange={setActiveTab}
        variant="vendor"
      />

      {/* Payout Agreement Dialog — shown before Stripe onboarding (and
          re-used as the first-step blocker for fresh vendors — Phase 4.5) */}
      <Dialog
        open={showPayoutAgreement}
        onOpenChange={(open) => {
          if (!open) {
            setShowPayoutAgreement(false);
            setPayoutAgreementChecked(false);
            // Reset intent back to the default so a later Stripe-connect
            // click doesn't accidentally take the onboarding branch.
            setAgreementIntent('stripe');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-emerald-600" />
              Platform Fee Agreement
            </DialogTitle>
            <DialogDescription>
              {agreementIntent === 'onboarding'
                ? 'Accept this to start setting up your profile.'
                : 'Please review and accept before setting up payouts.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-800 font-medium mb-1">You keep 97% of your earnings — a 3% platform fee applies to completed bookings</p>
              <p className="text-xs text-gray-500">This fee is automatically deducted from each completed booking payment and covers platform maintenance, payment processing support, and customer acquisition.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="payout-agreement-checkbox">
              <input
                type="checkbox"
                checked={payoutAgreementChecked}
                onChange={(e) => setPayoutAgreementChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">
                I agree to Celey&apos;s{' '}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsDialog(true); }}
                  className="text-emerald-600 underline hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 rounded-sm"
                  data-testid="agreement-terms-link"
                >
                  Terms of Service
                </button>
                {' '}and Fee Structure
              </span>
            </label>
            <button
              type="button"
              onClick={handleAcceptPayoutAgreement}
              disabled={!payoutAgreementChecked || acceptingAgreement}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              data-testid="payout-agreement-continue"
            >
              {acceptingAgreement
                ? 'Processing...'
                : agreementIntent === 'onboarding'
                  ? 'Continue to Profile Setup'
                  : 'Continue to Stripe Setup'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 4.7 — Terms of Service shown in-place over the agreement
          dialog. Radix handles stacking + focus restoration, so closing this
          returns the user cleanly to the agreement flow. */}
      <TermsDialog open={showTermsDialog} onOpenChange={setShowTermsDialog} />
    </div>
  );
};

export default VendorDashboard;
